import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ChevronUp, Calculator, FileText, Save } from "lucide-react";
import { buildPlanContext } from "./Chapter13PlanGenerator";
import { filingDebtors as getFilingDebtors } from "../utils/debtors";
import { buildPlanCreditors, claimReconciliationSummary } from "../utils/proofOfClaims";
import MailingMatrixServiceSelector from './components/MailingMatrixServiceSelector';
import Chapter13ModificationModal from './components/Chapter13ModificationModal';
import { BestInterestTemplateControls, TaxRefundTemplateControls } from './components/PlanLanguageTemplates';
import { hasNonstandardProvision } from '../utils/chapter13PlanLanguage';
const API_URL = process.env.REACT_APP_API_URL;

// --- helpers (copied from FirmCreditors style) ---
const lc = (v) => (v ?? "").toString().trim().toLowerCase();
function isStudentLoan(cred){
  const t = (cred?.account_type||'').toString().toLowerCase();
  const name = (cred?.account_name||'').toString().toLowerCase();
  return t.includes('student') || name.includes('student loan');
}

function splitPriorityParts(creditors){
  let priorityPrincipal = 0;      // portion actually priority and must be paid in full
  let nonPriorityRemainder = 0;   // remainder that goes to unsecured base
  for (const c of (creditors||[])) {
    if (String(c.debt_type||'').toLowerCase() !== 'priority') continue;
    const bal = Number(c.balance||0);
    const pri = Number(c.priority_amount||0);
    priorityPrincipal    += Math.max(0, Math.min(bal, pri));
    nonPriorityRemainder += Math.max(0, bal - pri);
  }
  return { priorityPrincipal, nonPriorityRemainder };
}
const toMoney = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return "";
  return Number.parseFloat(n).toFixed(2);
};
const toTemplateMoney = (v) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0.00";
};
const numOr = (v, fallback) => {
  if (v === "" || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const nOrNull = (v) => (v === "" || v === undefined || v === null ? null : Number(v));
const iOrNull = (v) => (v === "" || v === undefined || v === null ? null : (Number.isFinite(Number(v)) ? parseInt(v, 10) : null));
const computeMonthlyPayment = (principal, annualRatePct, months) => {
  const P = Number(principal) || 0;
  const n = Number(months) || 0;
  const r = (Number(annualRatePct) || 0) / 100 / 12;
  if (P <= 0 || n <= 0) return 0;
  if (r === 0) return +(P / n).toFixed(2);
  const payment = (P * r) / (1 - Math.pow(1 + r, -n));
  return +payment.toFixed(2);
};

const toDateInputValue = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0'),
].join('-');

export const getAmendedPlanScheduleDefaults = (fromDate = new Date()) => {
  // Use local calendar dates at noon so daylight-saving changes cannot shift
  // the date shown by the browser's date input.
  const firstEligibleDate = new Date(
    fromDate.getFullYear(),
    fromDate.getMonth(),
    fromDate.getDate(),
    12,
  );
  firstEligibleDate.setDate(firstEligibleDate.getDate() + 21);

  const daysUntilTuesday = (2 - firstEligibleDate.getDay() + 7) % 7;
  const meetingDate = new Date(firstEligibleDate);
  meetingDate.setDate(meetingDate.getDate() + daysUntilTuesday);

  const confirmationDate = new Date(meetingDate);
  confirmationDate.setDate(confirmationDate.getDate() + 7);

  return {
    meetingDate: toDateInputValue(meetingDate),
    meetingTime: '08:30',
    confirmationDate: toDateInputValue(confirmationDate),
  };
};

export const shouldIncludeExecutoryPayment = (creditor, includeDirectPay = false) => (
  creditor?.chapter_13_assumption === true
  && (includeDirectPay || !creditor?.chapter_13_direct_pay)
);

const STUDENT_LOAN_DEBTOR_TERMS = {
  debtor1: {
    subject: 'Debtor 1', possessive: "Debtor 1's", be: 'is', do: 'does', make: 'makes', have: 'has', direct: 'DEBTOR 1',
  },
  debtor2: {
    subject: 'Debtor 2', possessive: "Debtor 2's", be: 'is', do: 'does', make: 'makes', have: 'has', direct: 'DEBTOR 2',
  },
  both: {
    subject: 'Debtors', possessive: "Debtors'", be: 'are', do: 'do', make: 'make', have: 'have', direct: 'DEBTORS',
  },
};

const newTemplateVariables = () => ({
  creditor: '',
  servicer: '',
  sourceCreditorId: '',
  studentLoanPaymentMethod: 'through_plan',
  debtorSelection: 'debtor1',
});

export function buildStudentLoanTreatmentProvision({
  creditor,
  servicer,
  paymentMethod = 'through_plan',
  debtorSelection = 'debtor1',
}) {
  const holder = String(creditor || '').trim() || '[Creditor]';
  const loanServicer = String(servicer || '').trim() || '[Servicer]';
  const terms = STUDENT_LOAN_DEBTOR_TERMS[debtorSelection] || STUDENT_LOAN_DEBTOR_TERMS.debtor1;
  const paidDirect = paymentMethod === 'direct';
  const paymentLanguage = paidDirect
    ? `DIRECT BY ${terms.direct}`
    : 'THROUGH THE CHAPTER 13 PLAN AS A GENERAL UNSECURED CREDITOR';

  const paragraphs = [
    'Student Loans',
    `1. ${terms.subject} ${terms.be} not seeking, nor does this Plan provide for, any discharge, in whole or in part, of ${terms.possessive} student loan obligations under 11 U.S.C. § 523(a)(8). However, ${terms.subject} reserves the right to file an adversary proceeding seeking a hardship discharge.`,
    `2. ${terms.possessive} student loans are held by ${holder} and serviced by ${loanServicer}. During the bankruptcy, ${terms.possessive} student loans are to be paid ${paymentLanguage}.`,
    `3. Pursuant to 34 C.F.R. § 685.209(k)(4)(iv)(K), ${terms.subject} shall receive a month of credit toward loan forgiveness for each month ${terms.subject} ${terms.make} a required payment under a confirmed Chapter 13 Plan. Pursuant to this regulation, ${terms.subject} ${terms.do} not need to be enrolled in an income-driven repayment plan, nor does this Plan need to provide for separate treatment of the claim, and such claim shall be treated as a general unsecured claim. The creditor will credit the months toward forgiveness when it is notified and provided documentation that ${terms.subject} made the required payments in the approved bankruptcy at the time of ${terms.possessive} discharge.`,
    `4. The creditor shall accept payments made by ${terms.subject} while this case is pending in Chapter 13. The creditor shall provide ${terms.subject} statements concerning periodic payments and any other written communications that are provided to nonbankruptcy borrowers in the ordinary course of servicing a student loan.`,
    `5. It shall not be deemed a violation of the automatic stay or other applicable state or federal laws for the student loan creditor or its servicer(s) to send ${terms.subject} normal monthly statements regarding IDR payments due and any other communications, including notices of late payments or delinquency. These communications may include telephone calls and emails if ${terms.subject} ${terms.have} agreed to accept electronic communications under the normal processes established by the student loan creditor or its servicer(s).`,
  ];

  if (paidDirect) {
    paragraphs.push(
      `6. ${terms.subject} shall be allowed to continue to participate in an income-driven repayment plan ("IDR plan") provided by ${holder} pursuant to its IDR program, or ${terms.subject} shall be allowed to seek enrollment in an IDR plan.\na. ${terms.subject} shall annually recertify and remain enrolled in the IDR plan while the Chapter 13 case is pending.\nb. ${terms.subject} shall, within 30 days following the annual determination of an updated payment or approval of a post-petition IDR plan, notify the Trustee of such payment and file a modification of the Plan if necessary.\nc. ${terms.subject} shall be allowed to consolidate student loan debt without further order of the Court but shall notify the Trustee within 30 days of any consolidation, including providing a copy of the contract. Consolidation of the student loan debt after the filing of bankruptcy will render the debt a post-petition debt and it cannot be the subject of an adversary proceeding in the pre-petition bankruptcy case.`
    );
  }

  return paragraphs.join('\n');
}

const legacyFuturePaymentChange = (plan, debtorNumber) => {
  const change = {
    debtor_number: debtorNumber,
    change_date: plan?.[`future_payment_change_date_debtor_${debtorNumber}`] || '',
    amount: plan?.[`future_payment_debtor_${debtorNumber}`] ?? '',
    period: plan?.[`future_payment_period_debtor_${debtorNumber}`] || '',
    source: plan?.[`future_payment_source_debtor_${debtorNumber}`] || '',
    position: 0,
  };
  return [change.change_date, change.amount, change.period, change.source].some(
    value => value !== null && value !== undefined && value !== '',
  ) ? change : null;
};

export const futurePaymentChangesFromPlan = (plan) => {
  if (Array.isArray(plan?.future_payment_changes)) {
    return plan.future_payment_changes.map((change, index) => ({
      ...change,
      debtor_number: Number(change?.debtor_number) === 2 ? 2 : 1,
      change_date: change?.change_date || '',
      amount: change?.amount ?? '',
      period: change?.period || '',
      source: change?.source || '',
      position: Number.isFinite(Number(change?.position)) ? Number(change.position) : index,
    }));
  }
  return [legacyFuturePaymentChange(plan, 1), legacyFuturePaymentChange(plan, 2)].filter(Boolean);
};

const normalizePlanFuturePayments = (plan) => (
  plan ? { ...plan, future_payment_changes: futurePaymentChangesFromPlan(plan) } : plan
);

const serializedFuturePaymentChanges = (plan) => {
  const positions = { 1: 0, 2: 0 };
  return futurePaymentChangesFromPlan(plan)
    .filter(change => [change.change_date, change.amount, change.period, change.source].some(
      value => value !== null && value !== undefined && value !== '',
    ))
    .map((change) => {
      const debtorNumber = Number(change.debtor_number) === 2 ? 2 : 1;
      const serialized = {
        debtor_number: debtorNumber,
        change_date: change.change_date || null,
        amount: nOrNull(change.amount),
        period: change.period || null,
        source: change.source || null,
        position: positions[debtorNumber],
      };
      positions[debtorNumber] += 1;
      return serialized;
    });
};

function FuturePaymentChangesEditor({ debtorNumber, changes, onAdd, onChange, onRemove }) {
  const dates = changes.map(({ change }) => change.change_date || '')
    .filter(date => /^\d{4}-\d{2}-\d{2}$/.test(date)).sort();
  const durationDescription = change => {
    const date = change.change_date || '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'Enter the change date to preview its payment period.';
    const nextDate = dates.find(candidate => candidate > date);
    if (!nextDate) return 'Applies for the remainder of the term.';
    const [year, month, day] = nextDate.split('-');
    return `Applies until ${month}/${day}/${year}.`;
  };
  return (
    <div className="md:col-span-4 mt-2 p-3 border rounded bg-gray-50">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-xs font-semibold">Future Payment Changes (Debtor {debtorNumber})</div>
        <button
          type="button"
          className="px-3 py-1.5 text-xs font-medium border rounded bg-white hover:bg-gray-100"
          onClick={() => onAdd(debtorNumber)}
        >
          + Add Payment Change
        </button>
      </div>
      <p className="mb-3 text-xs text-gray-600">Each future payment applies until that debtor’s next scheduled payment-change date; the final amount applies for the remainder of the term.</p>
      {changes.length === 0 ? (
        <div className="text-xs text-gray-500">No future payment changes added.</div>
      ) : (
        <div className="space-y-3">
          {changes.map(({ change, index }, displayIndex) => (
            <div key={change.id || `${debtorNumber}-${index}`} className="p-2 border rounded bg-white">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs font-medium text-gray-600">Change {displayIndex + 1}</span>
                <button
                  type="button"
                  className="text-xs font-medium text-red-700 hover:underline"
                  onClick={() => onRemove(index)}
                >
                  Remove
                </button>
              </div>
              <p className="mb-2 text-xs text-gray-600">{durationDescription(change)}</p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <div>
                  <label className="block text-xs mb-1">Change Date</label>
                  <input
                    type="date"
                    className="w-full border rounded px-2 py-1.5"
                    value={change.change_date || ''}
                    onChange={(event) => onChange(index, 'change_date', event.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1">Future Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border rounded px-2 py-1.5 text-right"
                    value={change.amount ?? ''}
                    onChange={(event) => onChange(index, 'amount', event.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1">Future Period</label>
                  <select
                    className="w-full border rounded px-2 py-1.5"
                    value={change.period || ''}
                    onChange={(event) => onChange(index, 'period', event.target.value)}
                  >
                    <option value="">—</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="semi-monthly">Semi-Monthly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs mb-1">Future Source</label>
                  <select
                    className="w-full border rounded px-2 py-1.5"
                    value={change.source || ''}
                    onChange={(event) => onChange(index, 'source', event.target.value)}
                  >
                    <option value="">—</option>
                    <option value="direct">Direct</option>
                    <option value="payroll">Payroll</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- plan API helpers (robust to either API style) ---
const buildPlanPayload = (caseId, plan, includeCase = false, extras = {}) => {
  const futurePaymentChanges = serializedFuturePaymentChanges(plan);
  const firstDebtor1Change = futurePaymentChanges.find(change => change.debtor_number === 1);
  const firstDebtor2Change = futurePaymentChanges.find(change => change.debtor_number === 2);
  return ({
  ...(includeCase ? { case: caseId } : {}),
  debt_calculation_source: plan?.debt_calculation_source || 'scheduled',
  plan_length: iOrNull(plan?.plan_length),
  payment_debtor_1: nOrNull(plan?.payment_debtor_1),
  payment_period_debtor_1: plan?.payment_period_debtor_1 || null,
  payment_source_debtor_1: plan?.payment_source_debtor_1 || null,
  future_payment_change_date_debtor_1: firstDebtor1Change?.change_date || null,
  future_payment_debtor_1: firstDebtor1Change?.amount ?? null,
  future_payment_period_debtor_1: firstDebtor1Change?.period || null,
  future_payment_source_debtor_1: firstDebtor1Change?.source || null,

  payment_debtor_2: nOrNull(plan?.payment_debtor_2),
  payment_period_debtor_2: plan?.payment_period_debtor_2 || null,
  payment_source_debtor_2: plan?.payment_source_debtor_2 || null,
  future_payment_change_date_debtor_2: firstDebtor2Change?.change_date || null,
  future_payment_debtor_2: firstDebtor2Change?.amount ?? null,
  future_payment_period_debtor_2: firstDebtor2Change?.period || null,
  future_payment_source_debtor_2: firstDebtor2Change?.source || null,
  future_payment_changes: futurePaymentChanges,

  refund_payment: plan?.refund_payment || 'all',
  refund_payment_other_description: plan?.refund_payment === 'other' ? (plan?.refund_payment_other_description || '') : null,

  dso_treatment: plan?.dso_treatment || null,

  unsecured_treatment: plan?.unsecured_treatment || null,
  unsecured_percentage: plan?.unsecured_treatment === 'percent' ? nOrNull(plan?.unsecured_percentage) : null,
  unsecured_other_description: plan?.unsecured_treatment === 'other' ? (plan?.unsecured_other_description || null) : null,
  best_interest_amount: plan?.unsecured_treatment === 'bic' ? nOrNull(plan?.best_interest_amount) : null,
  pdi_amount: plan?.unsecured_treatment === 'pdi' ? nOrNull(plan?.pdi_amount) : null,
  other_amount: plan?.unsecured_treatment === 'other' ? nOrNull(plan?.other_amount) : null,

  tax_claims_full: !!plan?.tax_claims_full,
  tax_claims_none: !!plan?.tax_claims_none,
  consumer_claims_none: !!plan?.consumer_claims_none,
  consumer_claims_full: !!plan?.consumer_claims_full,
  consumer_claims_per_unsecured_treatment: !!plan?.consumer_claims_per_unsecured_treatment,
  ...extras,
  });
};

const fetchPlanRobust = async ({ API_URL, caseId, token }) => {
  // Try case-scoped APIView first
  const tryGet = [
    `${API_URL}/case/${caseId}/chapter-13-plan/`,
    `${API_URL}/case/${caseId}/chapter-13-plans/`, // ViewSet case list
    `${API_URL}/chapter-13-plans/?case=${caseId}`, // global list filter if supported
  ];

  for (const url of tryGet) {
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 404) continue;
      if (res.ok) {
        const data = await res.json();
        // case list endpoints may return an array; normalize to single plan
        if (Array.isArray(data)) return data[0] || null;
        return data;
      }
    } catch (e) {}
  }
  return null; // none found
};

const createPlanRobust = async ({ API_URL, caseId, token, initial }) => {
  const tryPost = [
    { url: `${API_URL}/case/${caseId}/chapter-13-plan/`, method: 'POST', includeCase: false },
    { url: `${API_URL}/case/${caseId}/chapter-13-plans/`, method: 'POST', includeCase: false },
    { url: `${API_URL}/chapter-13-plans/`, method: 'POST', includeCase: true },
  ];
  let lastStatus = null;
  let lastBody = null;
  for (const t of tryPost) {
    try {
      const payload = buildPlanPayload(caseId, initial || {}, t.includeCase, {});
      const res = await fetch(t.url, {
        method: t.method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) return await res.json();
      lastStatus = res.status;
      try { lastBody = await res.text(); } catch (_) {}
      if ([400, 405, 409].includes(res.status)) {
        const existing = await fetchPlanRobust({ API_URL, caseId, token });
        if (existing) return existing;
      }
    } catch (e) {
      lastBody = e.message;
    }
  }
  const msg = lastStatus ? `Failed to create Chapter 13 plan (last status ${lastStatus}${lastBody ? `: ${lastBody}` : ''})` : 'Failed to create Chapter 13 plan';
  throw new Error(msg);
};

const savePlanRobust = async ({ API_URL, caseId, token, plan, editedCreditors, dirtyIds }) => {
  // Save only creditors that changed, and only if secured or executory
  const patchChangedCreditors = async () => {
    if (!editedCreditors || !dirtyIds || dirtyIds.size === 0) return;

    const toSave = editedCreditors.filter(
      (c) => dirtyIds.has(c.id) && ['secured', 'executory'].includes(lc(c.debt_type))
    );

    for (const c of toSave) {
      const payload = {
        chapter_13_secured_treatment: (c.chapter_13_secured_treatment || '').toLowerCase(),
        chapter_13_direct_pay: !!c.chapter_13_direct_pay,
        chapter_13_assumption: c.chapter_13_assumption ?? null,
        arrearage: c.arrearage,
        // maintenance
        chapter_13_maintenance_payment_amount: nOrNull(c.chapter_13_maintenance_payment_amount),
        chapter_13_maintenance_arrearage_payment: nOrNull(c.chapter_13_maintenance_arrearage_payment),
        chapter_13_maintenance_arrearage_months: iOrNull(c.chapter_13_maintenance_arrearage_months),
        chapter_13_maintenance_arrearage_interest_rate: nOrNull(c.chapter_13_maintenance_arrearage_interest_rate),
        // cramdown
        chapter_13_cramdown_secured_amount: nOrNull(c.chapter_13_cramdown_secured_amount),
        chapter_13_cramdown_interest_rate: nOrNull(c.chapter_13_cramdown_interest_rate),
        chapter_13_cramdown_months: iOrNull(c.chapter_13_cramdown_months),
        chapter_13_cramdown_payment: nOrNull(c.chapter_13_cramdown_payment),
        // 910
        chapter_13_910_secured_amount: nOrNull(c.chapter_13_910_secured_amount),
        chapter_13_910_interest_rate: nOrNull(c.chapter_13_910_interest_rate),
        chapter_13_910_months: iOrNull(c.chapter_13_910_months),
        chapter_13_910_payment: nOrNull(c.chapter_13_910_payment),
        // lien
        chapter_13_lien_secured_amount: nOrNull(c.chapter_13_lien_secured_amount),
        chapter_13_lien_other_lien_amount: nOrNull(c.chapter_13_lien_other_lien_amount),
        chapter_13_lien_interest_rate: nOrNull(c.chapter_13_lien_interest_rate),
        chapter_13_lien_months: iOrNull(c.chapter_13_lien_months),
        chapter_13_lien_payment: nOrNull(c.chapter_13_lien_payment),
        // third-party
        chapter_13_third_party: c.chapter_13_third_party ?? null,
      };

      await fetch(`${API_URL}/case/${caseId}/creditors/${c.id}/`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    }
  };

  if (plan?.id) {
    const tryPatch = [
      { url: `${API_URL}/case/${caseId}/chapter-13-plan/${plan.id}/`, includeCase: false },
      { url: `${API_URL}/chapter-13-plans/${plan.id}/`, includeCase: true },
    ];
    let lastErr;
    for (const t of tryPatch) {
      try {
        const payload = buildPlanPayload(caseId, plan, t.includeCase, {});
        const res = await fetch(t.url, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          await patchChangedCreditors();
          return await res.json();
        }
        lastErr = new Error(`PATCH ${t.url} → ${res.status}`);
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr || new Error('Failed to save plan');
  }

  // No plan.id → create then patch changed creditors
  const created = await createPlanRobust({ API_URL, caseId, token, initial: plan });
  await patchChangedCreditors();
  return created;
};

// --- asset + lien helpers ---
const effectiveAssetLinkValue = (l) => {
  const t = (l?.asset_type || '').toString().toLowerCase();
  if (t === 'realproperty' || t === 'vehicle') {
    return Number(l?.value_ownership_portion ?? l?.value_total ?? 0) || 0;
  }
  if (t === 'householditem') {
    return Number(l?.value ?? 0) || 0;
  }
  return Number(l?.amount ?? 0) || 0; // everything else
};

const sumLinkedAssetValues = (cred) => {
  const links = cred?.asset_links || [];
  return links.reduce((acc, l) => acc + effectiveAssetLinkValue(l), 0);
};

const linkedCollateralLabel = (cred) => {
  const links = cred?.asset_links || [];
  if (links.length === 0) return "Not linked";
  return links
    .map((link) => link.asset_label || link.asset_type_label || `${(link.asset_type || "Asset").replace(/_/g, " ")} #${link.object_id}`)
    .filter(Boolean)
    .join(", ");
};

const sumLinkedExemptions = (cred) => {
  const links = cred?.asset_links || [];
  return links.reduce((acc, l) => acc + (Number(l?.exemption_amount) || 0), 0);
};

const getLinkedObjectIds = (cred) => new Set((cred?.asset_links || []).map(l => `${l.object_id}|${l.asset_type}`));

const creditorsShareAnyAsset = (a, b) => {
  const A = getLinkedObjectIds(a);
  if (A.size === 0) return false;
  const B = getLinkedObjectIds(b);
  for (const id of A) if (B.has(id)) return true;
  return false;
};

const parseDate = (d) => (d ? new Date(d) : null);

const otherLiensTotal = (current, allCreds) => {
  // Sum balances of other creditors that share the same linked asset(s) and have an older date_opened
  const curDate = parseDate(current?.date_opened);
  return allCreds
    .filter(o => o.id !== current.id)
    .filter(o => creditorsShareAnyAsset(o, current))
    .filter(o => {
      if (!curDate) return true; // if current has no date, treat all others as older for safety
      const od = parseDate(o?.date_opened);
      if (!od) return true; // if other has no date, assume older
      return od < curDate;
    })
    .reduce((s, o) => s + (Number(o.balance) || 0), 0);
};

// ---- Liquidation helpers ----
const ch7TrusteeFee = (amount) => {
  const a = Math.max(0, Number(amount) || 0);
  let fee = 0;
  const tier1 = Math.min(a, 5000);
  fee += tier1 * 0.25;
  const tier2 = Math.min(Math.max(a - 5000, 0), 45000);
  fee += tier2 * 0.10;
  const tier3 = Math.min(Math.max(a - 50000, 0), 950000);
  fee += tier3 * 0.05;
  const tier4 = Math.max(a - 1000000, 0);
  fee += tier4 * 0.03;
  return fee;
};

// Build a map of assetKey -> { links: [link], totalValueForFees }
const groupAssetLinks = (creditors) => {
  const map = new Map();
  for (const c of creditors) {
    for (const l of (c.asset_links || [])) {
      const key = `${l.object_id}|${(l.asset_type||'').toLowerCase()}`;
      if (!map.has(key)) map.set(key, { asset_type: (l.asset_type||'').toLowerCase(), links: [], creditors: new Set() });
      const entry = map.get(key);
      entry.links.push(l);
      entry.creditors.add(c); // capture creditor objects to read balances
    }
  }
  return map;
};

// Compute Best-Interest (liquidation) amount per your spec
const computeBestInterestAmount = (creditors, assets) => {
  const balancesIdx = buildBalancesByAsset(creditors);
  const linkExIdx = buildLinkExemptionIndex(creditors);
  let total = 0;

  for (const a of (assets||[])) {
    const t = (a.asset_type||'').toLowerCase();
    const key = keyFrom(a.id, a.asset_type);
    const linkBal = balancesIdx.get(key) || 0;

    if (t === 'realproperty' || t === 'vehicle') {
      const valueTotalForFees = Number(a.value_total) || 0;
      const valuePortion = Number(a.value_ownership_portion) || 0;
      const ratio = valueTotalForFees > 0 ? (valuePortion / valueTotalForFees) : 0;
      const creditorPortion = linkBal * ratio;
      const saleFee = valueTotalForFees * 0.06;
      const trustee = ch7TrusteeFee(valueTotalForFees);
      const exemption = Number(a.exemption_amount) || linkExIdx.get(key) || 0;
      const liquidation = valuePortion - saleFee - exemption - creditorPortion - trustee;
      if (liquidation > 0) total += liquidation;
    } else if (t === 'householditem') {
      const v = Number(a.value) || 0;
      const saleFee = v * 0.06;
      const trustee = ch7TrusteeFee(v);
      const exemption = Number(a.exemption_amount) || linkExIdx.get(key) || 0;
      const creditorPortion = linkBal;
      const liquidation = v - saleFee - exemption - creditorPortion - trustee;
      if (liquidation > 0) total += liquidation;
    } else {
      const v = Number(a.amount) || 0;
      const saleFee = v * 0.06;
      const trustee = ch7TrusteeFee(v);
      const exemption = Number(a.exemption_amount) || linkExIdx.get(key) || 0;
      const creditorPortion = linkBal;
      const liquidation = v - saleFee - exemption - creditorPortion - trustee;
      if (liquidation > 0) total += liquidation;
    }
  }
  return +Number(total).toFixed(2);
};

// Compute Best Interest (BIC) per-asset breakdown
const computeBICBreakdown = (creditors, assets) => {
  const balancesIdx = buildBalancesByAsset(creditors);
  const linkExIdx = buildLinkExemptionIndex(creditors);
  const rows = [];
  let total = 0;

  for (const a of (assets || [])) {
    const t = (a.asset_type || '').toLowerCase();
    const key = keyFrom(a.id, a.asset_type);
    const linkBal = balancesIdx.get(key) || 0;

    let baseValue = 0, saleFee = 0, ch7Fee = 0, exemption = 0, creditorPortion = 0, liquidation = 0, label = '';

    if (t === 'realproperty' || t === 'vehicle') {
      const totalVal = Number(a.value_total) || 0;
      const portion = Number(a.value_ownership_portion) || 0;
      const ratio = totalVal > 0 ? portion / totalVal : 0;
      baseValue = portion;
      saleFee = totalVal * 0.06;
      ch7Fee = ch7TrusteeFee(totalVal);
      exemption = Number(a.exemption_amount) || linkExIdx.get(key) || 0;
      creditorPortion = linkBal * ratio;
      liquidation = portion - saleFee - exemption - creditorPortion - ch7Fee;
      label = `${t === 'realproperty' ? 'Real Property' : 'Vehicle'} (${a.id})`;
    } else if (t === 'householditem') {
      const v = Number(a.value) || 0;
      baseValue = v;
      saleFee = v * 0.06;
      ch7Fee = ch7TrusteeFee(v);
      exemption = Number(a.exemption_amount) || linkExIdx.get(key) || 0;
      creditorPortion = linkBal;
      liquidation = v - saleFee - exemption - creditorPortion - ch7Fee;
      label = `Household Item (${a.id})`;
    } else {
      const v = Number(a.amount) || 0;
      baseValue = v;
      saleFee = v * 0.06;
      ch7Fee = ch7TrusteeFee(v);
      exemption = Number(a.exemption_amount) || linkExIdx.get(key) || 0;
      creditorPortion = linkBal;
      liquidation = v - saleFee - exemption - creditorPortion - ch7Fee;
      const tName = (a.asset_type || 'Other').toString().replace(/\b\w/g, m => m.toUpperCase());
      label = `${tName} (${a.id})`;
    }

    const contributes = liquidation > 0;
    if (contributes) total += liquidation;

    rows.push({
      key,
      type: t,
      label,
      baseValue: +Number(baseValue).toFixed(2),
      saleFee: +Number(saleFee).toFixed(2),
      exemption: +Number(exemption).toFixed(2),
      creditorPortion: +Number(creditorPortion).toFixed(2),
      ch7Fee: +Number(ch7Fee).toFixed(2),
      liquidation: +Number(liquidation).toFixed(2),
      contributes,
    });
  }

  return { rows, total: +Number(total).toFixed(2) };
};
// ---- Case Assets (for BIC across ALL assets) ----
const keyFrom = (objId, type) => `${objId}|${(type||'').toLowerCase()}`;


// Index exemptions found on links (max per asset)
const buildLinkExemptionIndex = (creditors) => {
  const idx = new Map();
  for (const c of creditors) {
    for (const l of (c.asset_links||[])) {
      const key = keyFrom(l.object_id, l.asset_type);
      const ex = Number(l.exemption_amount) || 0;
      if (!idx.has(key)) idx.set(key, ex);
      else idx.set(key, Math.max(idx.get(key), ex));
    }
  }
  return idx;
};

// Index total balances per asset from links
const buildBalancesByAsset = (creditors) => {
  const idx = new Map();
  for (const c of creditors) {
    const bal = Number(c.balance) || 0;
    if (bal <= 0) continue;
    for (const l of (c.asset_links||[])) {
      const key = keyFrom(l.object_id, l.asset_type);
      idx.set(key, (idx.get(key) || 0) + bal);
    }
  }
  return idx;
};
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const fetchCaseAssets = async ({ API_URL, caseId, token }) => {
  const url = `${API_URL}/case/${caseId}/ch-13-assets/`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return [];
  return await res.json();
};

// --- period helpers ---
const periodsPerMonth = (period) => {
  switch ((period||'').toLowerCase()) {
    case 'weekly': return 52/12;      // ~4.3333
    case 'biweekly': return 26/12;    // ~2.1667
    case 'semi-monthly': return 24/12;// 2
    case 'monthly': return 1;
    default: return 1;
  }
};
const amountForPeriodFromMonthly = (monthly, period) => {
  const ppm = periodsPerMonth(period);
  return ppm ? +(Number(monthly)/ppm).toFixed(2) : 0;
};
const monthlyFromAmountAndPeriod = (amount, period) => {
  return +(Number(amount) * periodsPerMonth(period)).toFixed(2);
};
export default function Chapter13PlanPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  // Inline editors (modals)
  const [data, setData] = useState({
        debtsD: [], // array of secured creditors
        debtsE: [], // array of priority creditors
        debtsF: [], // array of nonpriority unsecured creditors
        debtsG: [],
        notifyEF: [], // array of others to notify
        allDebts: [],
        caseInfo: {}, // case metadata: number, name, etc.
        amended: false,
        amendedReason: '',
        amendedCreditors: [],
    });  
    const [showAmendedModal, setShowAmendedModal] = useState(false);
    const [amendedReason, setAmendedReason] = useState('');
    const [amendStep, setAmendStep] = useState(1);
    const [amendedCreditors, setAmendedCreditors] = useState([]);
    const [amendedMeetingDate, setAmendedMeetingDate] = useState('');
    const [amendedConfirmationDate, setAmendedConfirmationDate] = useState('');
    const [amendedMeetingTime, setAmendedMeetingTime] = useState('08:30');
    const [amendedConfirmationTime, setAmendedConfirmationTime] = useState('13:30');
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [serviceRecipients, setServiceRecipients] = useState([]);
    const [mailingMatrixService, setMailingMatrixService] = useState(null);
    const [caseData, setCaseData] = useState(null);
    const [showModificationModal, setShowModificationModal] = useState(false);
  const [caseAssets, setCaseAssets] = useState([]);
  const [creditorEditorOpen, setCreditorEditorOpen] = useState(false);
  const [assetEditorOpen, setAssetEditorOpen] = useState(false);
  const [editingCreditor, setEditingCreditor] = useState(null);
  const [editingAssetLink, setEditingAssetLink] = useState(null); // single link editor
  const [assetListMode, setAssetListMode] = useState(true); // list vs single form
  const token = localStorage.getItem("accessToken");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [plan, setPlan] = useState(null);
  const [creditors, setCreditors] = useState([]);
  const [proofOfClaims, setProofOfClaims] = useState([]);
  const [claimsLoadError, setClaimsLoadError] = useState("");
  const [creditors2, setCreditors2] = useState([]);
  const [dirtyCreditorIds, setDirtyCreditorIds] = useState(new Set());
  const [accordionOpen, setAccordionOpen] = useState({ secured: true, executory: true, settings: true, calc: true });

  // auto-split between D1/D2 for auto-calc
  const [splitPct, setSplitPct] = useState(100); // % of monthly target assigned to Debtor 1 (0..100)

  const [additionalPays, setAdditionalPays] = useState([]);
  const [nspList, setNspList] = useState([]);
  // Editable NSP textareas are drafts until their own Save button is used.
  // Keep the server-acknowledged descriptions separate so an unsaved draft
  // cannot falsely satisfy a mandatory notice's persistence requirement.
  const persistedNspRef = useRef([]);
  const bestInterestApplyRef = useRef(false);

  // --- load plan + creditors ---
  useEffect(() => {
    (async () => {
      try {
        const [caseRes, credRes] = await Promise.all([
            fetch(`${API_URL}/case/${caseId}/creditors/case-details/`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${API_URL}/case/${caseId}/creditors/`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);
        if (!caseRes.ok || !credRes.ok) {
            console.error("Failed to fetch case or creditors");
            return;
          }
         const caseJson = await caseRes.json();
          const credJson = await credRes.json();

          setCreditors(credJson);

          setData({
            caseInfo: caseJson,
            debtsD: credJson.filter(d => d.debt_type === "secured"),
            debtsE: credJson.filter(d => d.debt_type === "priority"),
            debtsF: credJson.filter(d => d.debt_type === "unsecured"),
            debtsG: credJson.filter(d => d.debt_type === "executory"),
            notifyEF: credJson.filter(d => d.additional_notified),
            allDebts : credJson,
          });

          const creditorAddresses = credJson.map(c => ({
        name: c.account_name || c.name,
        street1: c.street1 || '',
        street2: c.street2 || '',
        city: c.city || '',
        state: c.state || '',
        zip_code: c.zip_code || '',
        codebtor: c.codebtor || '',
        codebtor_street1:c.codebtor_street1 || '',
        codebtor_street2:c.codebtor_street2 || '',
        codebtor_state:c.codebtor_state || '',
        codebtor_zip_code:c.codebtor_zip_code|| '',
        codebtor_city:c.codebtor_city||'',
      }));
      setCreditors2(creditorAddresses);

        // Prefill attorney/trustee from Case Overview so calculator reflects case values
        try {
          const ov = await fetchOverviewRobust({ API_URL, caseId, token });
          applyOverviewToState(ov, setAttorneyTotal, setAttorneyPaid, setTrusteePct);
        } catch (_) {}

        try {
          const assets = await fetchCaseAssets({ API_URL, caseId, token });
          setCaseAssets(assets || []);
        } catch (_) {}
        let p = await fetchPlanRobust({ API_URL, caseId, token });
        // one extra peek at global list with ?case= before creating
        if (!p) {
          try {
            const alt = await fetch(`${API_URL}/chapter-13-plans/?case=${caseId}`, { headers: { Authorization: `Bearer ${token}` } });
            if (alt.ok) {
              const arr = await alt.json();
              if (Array.isArray(arr) && arr.length) p = arr[0];
            }
          } catch (_) {}
        }
        if (!p) {
          // initialize sensible defaults then create server-side so it has an id
          const clientDefault = {
            case: caseId,
            plan_length: 60,
            refund_payment: 'all',
            unsecured_treatment: 'percent',
            unsecured_percentage: 0,
            tax_claims_full: true,
            tax_claims_none: false,
            consumer_claims_none: false,
            consumer_claims_full: false,
            consumer_claims_per_unsecured_treatment: true,
          };
          p = await createPlanRobust({ API_URL, caseId, token, initial: clientDefault });
        }
        setPlan(normalizePlanFuturePayments(p));
        try { if (p?.id) setAdditionalPays(await fetchAdditionalPayments(p.id)); } catch(_) {}
        try {
          if (p?.id) {
            const provisions = await fetchNSP(p.id);
            persistedNspRef.current = provisions.map(({ id, description }) => ({ id, description }));
            setNspList(provisions);
          }
        } catch(_) {}
      } finally {
        setLoading(false);
      }
    })();
  }, [caseId, token]);

  useEffect(() => {
    if (!caseId) return;
    (async () => {
      try {
        setClaimsLoadError("");
        const response = await fetch(`${API_URL}/case/${caseId}/proof-of-claim/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`Unable to load filed claims (${response.status})`);
        const payload = await response.json();
        setProofOfClaims(Array.isArray(payload) ? payload : []);
      } catch (error) {
        setClaimsLoadError(error.message || "Unable to load filed claims");
        setProofOfClaims([]);
      }
    })();
  }, [caseId, token]);

  useEffect(() => {
      if (!caseId) return;
      async function fetchAll() {
        try {
          const caseRes = await fetch(`${API_URL}/case/${caseId}/overview/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!caseRes.ok) throw new Error("Couldn't load case overview");
          setCaseData(await caseRes.json());
        } catch (e) {
          console.error(e);
        }
      }
      fetchAll();
    }, [caseId]);
  // --- Additional Payments API (robust endpoints) ---
  const fetchAdditionalPayments = async (planId) => {
    const tryUrls = [
      `${API_URL}/chapter-13-additional-payments/?chapter_13_plan=${planId}`
    ];
    for (const url of tryUrls) {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) continue;
        const data = await res.json();
        return Array.isArray(data) ? data : (data?.results || []);
      } catch(_){}
    }
    return [];
  };
  const createAdditionalPayment = async (planId, item) => {
    const payload = { chapter_13_plan: planId, source: item.source||'', amount: item.amount===''?null:Number(item.amount||0), date: item.date||null };
    const tryUrls = [ `${API_URL}/chapter-13-additional-payments/` ];
    for (const url of tryUrls) {
      const res = await fetch(url, { method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json'}, body: JSON.stringify(payload) });
      if (res.ok) return await res.json();
    }
    throw new Error('Failed to create additional payment');
  };
  const updateAdditionalPayment = async (planId, id, patch) => {
    const payload = { source: patch.source, amount: patch.amount===''?null:Number(patch.amount||0), date: patch.date||null };
    const tryUrls = [ `${API_URL}/chapter-13-additional-payments/${id}/` ];
    for (const url of tryUrls) {
      const res = await fetch(url, { method:'PATCH', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json'}, body: JSON.stringify(payload) });
      if (res.ok) return await res.json();
    }
    throw new Error('Failed to update additional payment');
  };
  const deleteAdditionalPayment = async (planId, id) => {
    const tryUrls = [ `${API_URL}/chapter-13-additional-payments/${id}/` ];
    for (const url of tryUrls) {
      const res = await fetch(url, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
      if (res.ok || res.status===204) return true;
    }
    throw new Error('Failed to delete additional payment');
  };

  // --- Non-Standard Provisions API (robust endpoints) ---
  const fetchNSP = async (planId) => {
    const tryUrls = [ `${API_URL}/chapter-13-nonstandard-provisions/?chapter_13_plan=${planId}`, `${API_URL}/chapter-13-nonstandard-provisions/` ];
    for (const url of tryUrls) {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) continue;
        const data = await res.json();
        return Array.isArray(data) ? data : (data?.results || []);
      } catch(_){}
    }
    return [];
  };
  const createNSP = async (planId, item) => {
    const payload = { chapter_13_plan: planId, description: item.description||'' };
    const tryUrls = [ `${API_URL}/chapter-13-nonstandard-provisions/` ];
    for (const url of tryUrls) {
      const res = await fetch(url, { method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json'}, body: JSON.stringify(payload) });
      if (res.ok) {
        const created = await res.json();
        persistedNspRef.current = [
          ...persistedNspRef.current.filter(row => String(row.id) !== String(created.id)),
          { id: created.id, description: created.description ?? payload.description },
        ];
        return created;
      }
    }
    throw new Error('Failed to create non-standard provision');
  };
  const updateNSP = async (planId, id, patch) => {
    const payload = { description: patch.description||'' };
    const tryUrls = [ `${API_URL}/chapter-13-nonstandard-provisions/${id}/` ];
    for (const url of tryUrls) {
      const res = await fetch(url, { method:'PATCH', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json'}, body: JSON.stringify(payload) });
      if (res.ok) {
        const saved = await res.json();
        persistedNspRef.current = [
          ...persistedNspRef.current.filter(row => String(row.id) !== String(id)),
          { id: saved.id ?? id, description: saved.description ?? payload.description },
        ];
        return saved;
      }
    }
    throw new Error('Failed to update non-standard provision');
  };
  const deleteNSP = async (planId, id) => {
    const tryUrls = [ `${API_URL}/chapter-13-nonstandard-provisions/${id}/` ];
    for (const url of tryUrls) {
      const res = await fetch(url, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
      if (res.ok || res.status===204) {
        persistedNspRef.current = persistedNspRef.current.filter(row => String(row.id) !== String(id));
        return true;
      }
    }
    throw new Error('Failed to delete non-standard provision');
  };
  const applyBestInterestTemplate = async (result) => {
    if (bestInterestApplyRef.current) return;
    bestInterestApplyRef.current = true;
    try {
      if (result.nonstandardProvision && !hasNonstandardProvision(persistedNspRef.current, result.nonstandardProvision)) {
        if (!plan?.id) throw new Error('Save the plan before adding the required Section 8.1 notice.');
        // Save the mandatory notice first. A failed save must not leave joint
        // treatment in the plan without its corresponding Section 8.1 notice.
        const created = await createNSP(plan.id, { description: result.nonstandardProvision });
        // Preserve every editable draft. A local unsaved paragraph containing
        // the same words is not the newly persisted standalone notice row.
        setNspList(previous => previous.some(row => String(row.id) === String(created.id))
          ? previous : [...previous, created]);
      }
      setPlan(previous => ({
        ...(previous || {}),
        unsecured_treatment: 'other',
        unsecured_other_description: result.description,
        other_amount: result.otherAmount,
      }));
    } finally {
      bestInterestApplyRef.current = false;
    }
  };
  const NSP_TEMPLATES = [
    {
      id: 'student_loan',
      name: 'Student Loan Treatment',
      text: '',
    },
    {
      id: 'mortgage_outside',
      name: 'Mortgage Paid Outside of Plan',
      text: `Mortgage paid outside of plan.

During the pendency of the Chapter 13 case, Debtors’ counsel will be responsible for reviewing the mortgage proof of claim, notice of fees, notice of payment change, and filing any objections needed. Debtors’ counsel will also review any response to the notice of final cure and file the motion to deem the mortgage current at the end of this case.`
    },
    // add more templates here later
  ];
  const fillTemplate = (text, vars) => {
    return (text||'').replace(/<<\s*creditor\s*>>/gi, vars.creditor||'[Creditor]').replace(/<<\s*servicer\s*>>/gi, vars.servicer||'[Servicer]');
  };
  const [tmplSel, setTmplSel] = useState('');
  const [tmplVars, setTmplVars] = useState(newTemplateVariables);

  const autoCalcDebtorPayments = () => {
    if (!plan) return;
    const monthlyTarget = Number(totals.planPayment) || 0;
    const d1Share = monthlyTarget * (Math.max(0, Math.min(100, Number(splitPct))) / 100);
    const d2Share = monthlyTarget - d1Share;

    const p1 = plan.payment_period_debtor_1 || 'monthly';
    const p2 = plan.payment_period_debtor_2 || 'monthly';

    const newD1 = amountForPeriodFromMonthly(d1Share, p1);
    const newD2 = amountForPeriodFromMonthly(d2Share, p2);

    setPlan({
      ...plan,
      payment_debtor_1: toMoney(newD1),
      payment_debtor_2: toMoney(newD2),
    });
  };

  const addFuturePaymentChange = (debtorNumber) => {
    setPlan((currentPlan) => {
      const currentChanges = futurePaymentChangesFromPlan(currentPlan);
      return {
        ...(currentPlan || {}),
        future_payment_changes: [
          ...currentChanges,
          {
            debtor_number: debtorNumber,
            change_date: '',
            amount: '',
            period: '',
            source: '',
            position: currentChanges.filter(
              change => Number(change.debtor_number) === debtorNumber,
            ).length,
          },
        ],
      };
    });
  };

  const updateFuturePaymentChange = (index, field, value) => {
    setPlan((currentPlan) => ({
      ...(currentPlan || {}),
      future_payment_changes: futurePaymentChangesFromPlan(currentPlan).map(
        (change, changeIndex) => (
          changeIndex === index ? { ...change, [field]: value } : change
        ),
      ),
    }));
  };

  const removeFuturePaymentChange = (index) => {
    setPlan((currentPlan) => ({
      ...(currentPlan || {}),
      future_payment_changes: futurePaymentChangesFromPlan(currentPlan).filter(
        (_, changeIndex) => changeIndex !== index,
      ),
    }));
  };

  const futurePaymentChangesForDebtor = (debtorNumber) => (
    futurePaymentChangesFromPlan(plan)
      .map((change, index) => ({ change, index }))
      .filter(({ change }) => Number(change.debtor_number) === debtorNumber)
  );

  const secured = useMemo(() => creditors.filter(c => lc(c.debt_type) === "secured"), [creditors]);
  const executory = useMemo(() => creditors.filter(c => lc(c.debt_type) === "executory"), [creditors]);
  const calculationCreditors = useMemo(
    () => buildPlanCreditors(
      creditors,
      proofOfClaims,
      plan?.debt_calculation_source || "scheduled",
    ),
    [creditors, proofOfClaims, plan?.debt_calculation_source],
  );
  const calculationSecured = useMemo(
    () => calculationCreditors.filter(c => lc(c.debt_type) === "secured"),
    [calculationCreditors],
  );
  const calculationExecutory = useMemo(
    () => calculationCreditors.filter(c => lc(c.debt_type) === "executory"),
    [calculationCreditors],
  );
  const claimsSummary = useMemo(
    () => claimReconciliationSummary(proofOfClaims),
    [proofOfClaims],
  );
  const studentLoanCreditors = useMemo(() => creditors.filter(isStudentLoan), [creditors]);
  const filingDebtors = useMemo(
    () => getFilingDebtors(caseData?.debtors),
    [caseData],
  );

  const selectedStudentLoanCreditor = studentLoanCreditors.find(
    creditor => String(creditor.id) === String(tmplVars.sourceCreditorId)
  );

  const pullServicerFromCreditor = () => {
    if (!selectedStudentLoanCreditor) {
      alert('Select a student loan creditor record first.');
      return;
    }
    setTmplVars(current => ({
      ...current,
      servicer: selectedStudentLoanCreditor.account_name || '',
    }));
  };

const updateCreditorLocal = (id, patch) => {
  setCreditors(prev => prev.map(c => (c.id === id ? { ...c, ...patch } : c)));
  setDirtyCreditorIds(prev => {
    const next = new Set(prev);
    next.add(id);
    return next;
  });
};

  // --- Inline editor helpers ---
  const openCreditorEditor = (c) => {
    setEditingCreditor({ ...c });
    setCreditorEditorOpen(true);
  };
  const closeCreditorEditor = () => {
    setCreditorEditorOpen(false);
    setEditingCreditor(null);
  };

  const openAssetEditor = (c) => {
    setEditingCreditor(c); // keep context
    setEditingAssetLink(null);
    setAssetListMode(true);
    setAssetEditorOpen(true);
  };
  const editThisAssetLink = (link) => {
    setEditingAssetLink({ ...link });
    setAssetListMode(false);
  };
  const backToAssetList = () => {
    setEditingAssetLink(null);
    setAssetListMode(true);
  };
  const closeAssetEditor = () => {
    setAssetEditorOpen(false);
    setEditingAssetLink(null);
    setEditingCreditor(null);
    setAssetListMode(true);
  };

  // --- PATCH helpers ---
  const diffFields = (orig, next, keys) => {
    const out = {};
    keys.forEach(k => {
      if (next[k] !== orig[k]) out[k] = next[k];
    });
    return out;
  };

  // Normalize account_type casing based on debt_type
  const normalizeAccountType = (debtType, accountType) => {
    const dt = (debtType || '').toString().toLowerCase();
    const at = (accountType || '').toString();
    const lower = at.toLowerCase();
    const options = {
      secured: ['mortgage','auto','judgment lien','statutory lien'],
      priority: ['tax','child support','intoxicated'],
      unsecured: ['medical','credit card','charge account','personal loan','student loan','collection','deficiency','unsecured'],
    };
    const list = options[dt] || [];
    const match = list.find(v => v === lower);
    return match ? match : at; // use canonical lowercase if it matches; otherwise keep original
  };

  const patchCreditor = async (orig, next) => {
    const numeric = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    const payload = {
      ...diffFields(orig, next, [
        'account_name','account_number','debt_type','account_type','who_owes','nature_of_lien','lien_description',
        'lien_book','lien_page','lien_case_number','lien_county','dispute_status','fdic_bank'
      ]),
      balance: numeric(next.balance),
      arrearage: numeric(next.arrearage),
      date_opened: next.date_opened || null,
    };
    // Normalize account_type to canonical value if possible
    if (Object.prototype.hasOwnProperty.call(next, 'account_type')) {
      payload.account_type = normalizeAccountType(next.debt_type, next.account_type);
    }
    const res = await fetch(`${API_URL}/case/${caseId}/creditors/${orig.id}/`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to update creditor');
    return await res.json();
  };

  const patchAssetLink = async (linkId, data) => {
    const numeric = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    const payload = {
      value_ownership_portion: numeric(data.value_ownership_portion),
      monthly_payment: numeric(data.monthly_payment),
      second_monthly_payment: numeric(data.second_monthly_payment),
      exemption_amount: numeric(data.exemption_amount),
      value_total: numeric(data.value_total),
      value: numeric(data.value),
      amount: numeric(data.amount),
    };

    // Try case-scoped endpoint first: /case/:caseId/asset-links/:id/
    const tryEndpoints = [
      `${API_URL}/case/${caseId}/asset-links/${linkId}/`,
      `${API_URL}/asset-links/${linkId}/`,
    ];

    let lastErr;
    for (const url of tryEndpoints) {
      try {
        const res = await fetch(url, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) return await res.json();
        lastErr = new Error(`PATCH ${url} → ${res.status}`);
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr || new Error('Failed to update asset link');
  };

  const saveCreditor = async (c) => {
    const payload = {
      chapter_13_secured_treatment: lc(c.chapter_13_secured_treatment || ""),
      chapter_13_direct_pay: !!c.chapter_13_direct_pay,
      chapter_13_assumption: c.chapter_13_assumption ?? null,
      // maintenance
      chapter_13_maintenance_payment_amount: nOrNull(c.chapter_13_maintenance_payment_amount),
      chapter_13_maintenance_arrearage_payment: nOrNull(c.chapter_13_maintenance_arrearage_payment),
      chapter_13_maintenance_arrearage_months: iOrNull(c.chapter_13_maintenance_arrearage_months),
      chapter_13_maintenance_arrearage_interest_rate: nOrNull(c.chapter_13_maintenance_arrearage_interest_rate),
      // cramdown
      chapter_13_cramdown_secured_amount: nOrNull(c.chapter_13_cramdown_secured_amount),
      chapter_13_cramdown_interest_rate: nOrNull(c.chapter_13_cramdown_interest_rate),
      chapter_13_cramdown_months: iOrNull(c.chapter_13_cramdown_months),
      chapter_13_cramdown_payment: nOrNull(c.chapter_13_cramdown_payment),
      // 910
      chapter_13_910_secured_amount: nOrNull(c.chapter_13_910_secured_amount),
      chapter_13_910_interest_rate: nOrNull(c.chapter_13_910_interest_rate),
      chapter_13_910_months: iOrNull(c.chapter_13_910_months),
      chapter_13_910_payment: nOrNull(c.chapter_13_910_payment),
      // lien strip
      chapter_13_lien_secured_amount: nOrNull(c.chapter_13_lien_secured_amount),
      chapter_13_lien_other_lien_amount: nOrNull(c.chapter_13_lien_other_lien_amount),
      chapter_13_lien_interest_rate: nOrNull(c.chapter_13_lien_interest_rate),
      chapter_13_lien_months: iOrNull(c.chapter_13_lien_months),
      chapter_13_lien_payment: nOrNull(c.chapter_13_lien_payment),
      // third
      chapter_13_third_party: c.chapter_13_third_party ?? null,
    };

    const res = await fetch(`${API_URL}/case/${caseId}/creditors/${c.id}/`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to save creditor");
    const updated = await res.json();
    updateCreditorLocal(c.id, updated);
  };
  const [includeStudentLoans, setIncludeStudentLoans] = useState(true);
  // --- calculator ---
  const [attorneyTotal, setAttorneyTotal] = useState(0);
  const [attorneyPaid, setAttorneyPaid] = useState(0);
  const [trusteePct, setTrusteePct] = useState(7); // typical 5–10%
  const [filingFeeTotal, setFilingFeeTotal] = useState(0);
  const [filingFeePaid, setFilingFeePaid] = useState(0);
  const [includeDirectPay, setIncludeDirectPay] = useState(false);
  const [householdSize, setHouseholdSize] = useState(1);

  // --- Case Overview helpers ---
  // Applies overview fields to state setters, including filing fee fields
  const applyOverviewToState = (overview, setAttorneyTotal, setAttorneyPaid, setTrusteePct) => {
    if (!overview) return;
    const at = overview.total_attorney_fees ?? overview.attorney_fees_total ?? overview.attorney_total;
    const ap = overview.fees_paid_prior ?? overview.attorney_fees_paid ?? overview.attorney_paid;
    const tp = overview.trustee_percentage ?? overview.trustee_percent ?? overview.trustee_pct;
    const fft = overview.filing_fee_amount ?? overview.filing_fees_total ?? overview.filing_fee_total;
    const ffp = overview.filing_fees_paid ?? overview.filing_fee_paid;
    if (at != null) setAttorneyTotal(at);
    if (ap != null) setAttorneyPaid(ap);
    if (tp != null) setTrusteePct(tp);
    if (fft != null) setFilingFeeTotal(fft);
    if (ffp != null) setFilingFeePaid(ffp);
  };

  // Fetch case overview (robust)
  const fetchOverviewRobust = async ({ API_URL, caseId, token }) => {
    const tryGet = [
      `${API_URL}/case/${caseId}/overview/`,
      `${API_URL}/case/${caseId}/`,
    ];
    for (const url of tryGet) {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.status === 404) continue;
        if (res.ok) return await res.json();
      } catch (e) {}
    }
    return null;
  };

  // Save case overview (PUT)
  const saveCaseOverview = async ({ API_URL, caseId, token, total_attorney_fees, fees_paid_prior, trustee_percentage, filing_fee_amount, filing_fees_paid }) => {
    const payload = {
      total_attorney_fees: total_attorney_fees === '' ? null : Number(total_attorney_fees),
      fees_paid_prior: fees_paid_prior === '' ? null : Number(fees_paid_prior),
      trustee_percentage: trustee_percentage === '' ? null : Number(trustee_percentage),
      filing_fee_amount: filing_fee_amount === '' ? null : Number(filing_fee_amount),
      filing_fees_paid: filing_fees_paid === '' ? null : Number(filing_fees_paid),
    };
    const res = await fetch(`${API_URL}/case/${caseId}/`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Failed to save case overview (${res.status})`);
    return await res.json();
  };

  const totals = useMemo(() => {

    const take = (c) => includeDirectPay || !c.chapter_13_direct_pay;

    // 1) Monthly secured/executory components (already present)
    const sumMaint = calculationSecured
      .filter(c => lc(c.chapter_13_secured_treatment) === "maintenance" && take(c))
      .reduce((s, c) => s + numOr(c.chapter_13_maintenance_payment_amount, 0) + numOr(c.chapter_13_maintenance_arrearage_payment, 0), 0);

    const sumCram = calculationSecured
      .filter(c => lc(c.chapter_13_secured_treatment) === "cramdown" && take(c))
      .reduce((s, c) => s + (numOr(c.chapter_13_cramdown_payment, 0) || computeMonthlyPayment(numOr(c.chapter_13_cramdown_secured_amount, 0), numOr(c.chapter_13_cramdown_interest_rate, 0), numOr(c.chapter_13_cramdown_months, 0))), 0);

    const sum910 = calculationSecured
      .filter(c => lc(c.chapter_13_secured_treatment) === "910" && take(c))
      .reduce((s, c) => s + (numOr(c.chapter_13_910_payment, 0) || computeMonthlyPayment(numOr(c.chapter_13_910_secured_amount, 0), numOr(c.chapter_13_910_interest_rate, 0), numOr(c.chapter_13_910_months, 0))), 0);

    const sumLien = calculationSecured
      .filter(c => lc(c.chapter_13_secured_treatment) === "lien" && take(c))
      .reduce((s, c) => s + (numOr(c.chapter_13_lien_payment, 0) || computeMonthlyPayment(numOr(c.chapter_13_lien_secured_amount, 0), numOr(c.chapter_13_lien_interest_rate, 0), numOr(c.chapter_13_lien_months, 0))), 0);

    const sumExec = calculationExecutory
      .filter(c => shouldIncludeExecutoryPayment(c, includeDirectPay))
      .reduce((s, c) => s + numOr(c.chapter_13_maintenance_payment_amount, 0) + numOr(c.chapter_13_maintenance_arrearage_payment, 0), 0);

    // 2) Priority debt: paid in full over plan length
    const planMonths = Math.max(1, numOr(plan?.plan_length, 0));
    const priorityCreds = calculationCreditors.filter(c => lc(c.debt_type) === 'priority');

    // Sum of explicit priority amounts (if provided). If a creditor has a priority_amount of 0,
    // that means there is *no* priority portion — do not fall back to full balance.
    const totalPriorityAmount = priorityCreds.reduce((s, c) => {
      const pri = numOr(c.priority_amount, null);
      return s + (pri == null ? 0 : pri);
    }, 0);

    // For creditors where priority_amount is truly missing (null/undefined), fall back to full balance
    // so they are fully treated as priority. Empty strings are treated as missing by numOr above.
    const totalPriorityBalanceFallback = priorityCreds.reduce((s, c) => {
      const hasExplicitPriority = !(c.priority_amount == null);
      return s + (hasExplicitPriority ? 0 : numOr(c.balance, 0));
    }, 0);

    const prioritySum = totalPriorityAmount + totalPriorityBalanceFallback;
    const monthlyPriorityFull = prioritySum / planMonths;

    // 3) Unsecured base = selected general unsecured claims + undersecured
    // deficiencies + the nonpriority part of priority claims.
    const unsecuredGeneral = calculationCreditors
      .filter(c => lc(c.debt_type) === 'unsecured' && (includeStudentLoans || !isStudentLoan(c)))
      .reduce((s, c) => s + numOr(c.balance, 0), 0);

    // b) undersecured portions from cramdown & lien
    const undersecuredFromSecured = calculationSecured.reduce((s, c) => {
      const t = lc(c.chapter_13_secured_treatment || '');
      if (t !== 'cramdown' && t !== 'lien') return s;
      const bal = numOr(c.balance, 0);
      const securedAmt = t === 'cramdown' ? numOr(c.chapter_13_cramdown_secured_amount, 0) : numOr(c.chapter_13_lien_secured_amount, 0);
      const deficiency = Math.max(0, bal - Math.min(bal, securedAmt));
      return s + deficiency;
    }, 0);

    // c) nonpriority portion of priority claims = (balance - priority_amount) if positive
    const nonPriorityPart = priorityCreds.reduce((s, c) => {
      const bal = numOr(c.balance, 0);
      const pri = numOr(c.priority_amount, 0);
      return s + Math.max(0, bal - pri);
    }, 0);

    const unsecuredBase = Math.max(0, unsecuredGeneral + undersecuredFromSecured + nonPriorityPart);

    // d) Compute unsecured monthly target based on treatment
    let monthlyUnsecured = 0;
    const treatment = lc(plan?.unsecured_treatment || '');

    if (treatment === 'full') {
      monthlyUnsecured = unsecuredBase / planMonths;
    } else if (treatment === 'percent') {
      const pct = Math.max(0, Math.min(100, numOr(plan?.unsecured_percentage, 0)));
      monthlyUnsecured = (unsecuredBase * (pct / 100)) / planMonths;
    } else if (treatment === 'bic') {
      const cap = Math.min(unsecuredBase, Math.max(0, numOr(plan?.best_interest_amount, 0)));
      monthlyUnsecured = cap / planMonths;
    } else if (treatment === 'pdi') {
      const cap = Math.min(unsecuredBase, Math.max(0, numOr(plan?.pdi_amount, 0)));
      monthlyUnsecured = cap / planMonths;
    } else if (treatment === 'other') {
      const cap = Math.min(unsecuredBase, Math.max(0, numOr(plan?.other_amount, 0)));
      monthlyUnsecured = cap / planMonths;
    } else {
      // 'pro rata' or unspecified: cannot determine fixed target — assume 0 for calculator
      monthlyUnsecured = 0;
    }

    // Attorney fees remaining monthly
    const attorneyRemaining = Math.max(0, numOr(attorneyTotal, 0) - numOr(attorneyPaid, 0));
    const filingRemaining = Math.max(0, numOr(filingFeeTotal, 0) - numOr(filingFeePaid, 0));

    // Base monthly before trustee fee
    const baseMonthly = sumMaint + sumCram + sum910 + sumLien + sumExec + monthlyPriorityFull + monthlyUnsecured + (attorneyRemaining / planMonths) + (filingRemaining / planMonths);

    // Trustee fee on top
    const trusteeFee = baseMonthly * (numOr(trusteePct, 0) / 100);
    const planPayment = baseMonthly + trusteeFee;

    return {
      sumMaint,
      sumCram,
      sum910,
      sumLien,
      sumExec,
      monthlyPriorityFull,
      unsecuredBase,
      monthlyUnsecured,
      undersecuredFromSecured,
      nonPriorityPart,
      attorneyRemaining,
      filingRemaining,
      trusteeFee,
      planPayment,
    };
  }, [calculationSecured, calculationExecutory, calculationCreditors, includeStudentLoans, includeDirectPay, attorneyTotal, attorneyPaid, filingFeeTotal, filingFeePaid, trusteePct, plan?.plan_length, plan?.unsecured_treatment, plan?.unsecured_percentage, plan?.best_interest_amount, plan?.pdi_amount, plan?.other_amount]);
const bicBreakdown = useMemo(() => computeBICBreakdown(calculationCreditors, caseAssets), [calculationCreditors, caseAssets]);
function downloadBlob(blob, fallbackName = 'download.docx', disposition) {
  // Try to extract filename from the Content-Disposition header
  let filename = fallbackName;
  if (disposition) {
    const match = /filename\*?=(?:UTF-8'')?"?([^\";]+)"?/i.exec(disposition);
    if (match && match[1]) filename = decodeURIComponent(match[1]);
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
async function handleGeneratePlan(selectedRecipients = serviceRecipients) {
  try {
    // 1) Save the current plan (and ensure it exists)
const savedPlan = await savePlanRobust({
  API_URL,
  caseId,
  token,
  plan,
  editedCreditors: creditors,
  dirtyIds: dirtyCreditorIds,
});
setPlan(normalizePlanFuturePayments(savedPlan));
setDirtyCreditorIds(new Set());

    // 2) Save case-level admin costs (attorney/trustee/filing)
    await saveCaseOverview({
      API_URL,
      caseId,
      token,
      total_attorney_fees: attorneyTotal,
      fees_paid_prior: attorneyPaid,
      trustee_percentage: trusteePct,
      filing_fee_amount: filingFeeTotal,
      filing_fees_paid: filingFeePaid,
    });

    // 3) Build amended payload to send to backend
    const amendedPayload = {
        amended: !!data.amended,
        amended_reason: data.amendedReason || '',

        // existing:
        amended_meeting_date: data.amendedMeetingDate || null,
        amended_confirmation_date: data.amendedConfirmationDate || null,

        // NEW time fields:
        amended_meeting_time: data.amendedMeetingTime || null,
        amended_confirmation_time: data.amendedConfirmationTime || null,

        // Aliases some templates/views expect:
        meeting_date: data.amendedMeetingDate || null,
        meeting_time: data.amendedMeetingTime || null,
        hearing_date: data.amendedConfirmationDate || null,
        hearing_time: data.amendedConfirmationTime || null,

        amended_creditors: (data.amendedCreditors || []).map(c => ({
          name: c.name || '',
          street1: c.street1 || '',
          street2: c.street2 || '',
          city: c.city || '',
          state: c.state || '',
          zip_code: c.zip_code || '',
          method: c.method || null,
        })),
        service_recipients: (selectedRecipients || []).map(c => ({
          name: c.name || '',
          street1: c.street1 || '',
          street2: c.street2 || '',
          city: c.city || '',
          state: c.state || '',
          zip_code: c.zip_code || '',
          method: c.method || null,
        })),
        mailing_matrix_id: mailingMatrixService?.enabled ? mailingMatrixService.matrix_id : null,
        mailing_matrix_method: mailingMatrixService?.enabled
          ? (mailingMatrixService.method || 'USM')
          : null,
      };

    // 4) Ask backend to generate the plan using DB context
    const url = `${API_URL}/case/${caseId}/chapter-13-plan/generate-docx/`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(amendedPayload),
    });

    // Error handling: prefer JSON message if available, else text
    if (!res.ok) {
      const ctErr = res.headers.get('content-type') || '';
      if (ctErr.toLowerCase().includes('application/json')) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.error || JSON.stringify(err));
      }
      const txt = await res.text().catch(() => '');
      throw new Error(`Generate failed (${res.status})${txt ? `: ${txt}` : ''}`);
    }

    // Prefer Content-Disposition (if present) to decide it's a file
    const disposition = res.headers.get('content-disposition') || '';
    const ct = (res.headers.get('content-type') || '').toLowerCase();

    // If it's a file stream (docx/pdf/zip/octet-stream) OR server set Content-Disposition → download as blob
    if (
      disposition ||
      /application\/(pdf|zip|octet-stream|vnd\.openxmlformats-officedocument\.wordprocessingml\.document)/.test(ct)
    ) {
      const blob = await res.blob();
      let fallback = 'chapter-13-plan.docx';
      if (ct.includes('zip')) fallback = 'chapter-13-plan.zip';
      if (ct.includes('pdf')) fallback = 'chapter-13-plan.pdf';
      downloadBlob(blob, fallback, disposition);
    } else if (ct.includes('application/json')) {
      // JSON payload with a URL
      const json = await res.json();
      const fileUrl = json?.pdf_url || json?.file_url;
      if (fileUrl) {
        const absolute = fileUrl.startsWith('http')
          ? fileUrl
          : `${API_URL.replace(/\/api\/?$/, '')}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
        window.open(absolute, '_blank', 'noopener,noreferrer');
      } else {
        alert('Plan generated but no file URL was returned.');
      }
    } else {
      // Fallback: try to treat it as a file anyway
      const blob = await res.blob();
      downloadBlob(blob, 'chapter-13-plan.docx', disposition);
    }
  } catch (e) {
    console.error(e);
    alert(e.message || 'Failed to generate plan');
  }
}

const handleSavePlan = async () => {
    setSaving(true);
    try {
      // Save the plan first (only plan fields)
      const saved = await savePlanRobust({
  API_URL,
  caseId,
  token,
  plan,
  editedCreditors: creditors,
  dirtyIds: dirtyCreditorIds,
});
setPlan(normalizePlanFuturePayments(saved));
setDirtyCreditorIds(new Set());

      // Save case-level attorney/trustee/filing fee
      await saveCaseOverview({
        API_URL,
        caseId,
        token,
        total_attorney_fees: attorneyTotal,
        fees_paid_prior: attorneyPaid,
        trustee_percentage: trusteePct,
        filing_fee_amount: filingFeeTotal,
        filing_fees_paid: filingFeePaid,
      });

      alert('Plan and case info saved');
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="mx-auto w-full max-w-6xl p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold text-indigo-700">Chapter 13 Plan</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowModificationModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50"
          >
            <FileText className="w-4 h-4"/> Plan Modifications
          </button>
          <button onClick={handleSavePlan} className="inline-flex items-center gap-2 px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700">
            <Save className="w-4 h-4"/> Save
          </button>
        </div>
      </div>
      <Chapter13ModificationModal
        caseId={caseId}
        token={token}
        apiUrl={API_URL}
        open={showModificationModal}
        onClose={() => setShowModificationModal(false)}
      />
{showAmendedModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow max-w-md w-full">
            {amendStep === 1 && (
              <>
                <h3 className="text-lg font-semibold mb-2">Amendments to Plan</h3>
                <label className="block text-sm font-medium mb-1">Reason for Amendment</label>
                <textarea
                  value={amendedReason}
                  onChange={e => setAmendedReason(e.target.value)}
                  className="w-full border rounded p-2"
                  rows={4}
                  placeholder="Describe why this plan is being amended..."
                />
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setAmendStep(2)}
                    className="px-4 py-2 bg-blue-600 text-white rounded"
                  >
                    Next
                  </button>
                </div>
              </>
            )}
            {amendStep === 2 && (
            <>
              <h3 className="text-lg font-semibold mb-2">Meeting &amp; Hearing Schedule</h3>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">341 Meeting Date</label>
                    <input
                      type="date"
                      value={amendedMeetingDate}
                      onChange={e => setAmendedMeetingDate(e.target.value)}
                      className="w-full border rounded px-2 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">341 Meeting Time</label>
                    <input
                      type="time"
                      value={amendedMeetingTime}
                      onChange={e => setAmendedMeetingTime(e.target.value)}
                      className="w-full border rounded px-2 py-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Confirmation Hearing Date</label>
                    <input
                      type="date"
                      value={amendedConfirmationDate}
                      onChange={e => setAmendedConfirmationDate(e.target.value)}
                      className="w-full border rounded px-2 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Confirmation Hearing Time</label>
                    <input
                      type="time"
                      value={amendedConfirmationTime}
                      onChange={e => setAmendedConfirmationTime(e.target.value)}
                      className="w-full border rounded px-2 py-2"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-between">
                <button
                  onClick={() => setAmendStep(1)}
                  className="px-4 py-2 bg-gray-200 rounded"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    setShowAmendedModal(false);
                    setData({
                      ...data,
                      amendedReason,
                      amendedMeetingDate,
                      amendedMeetingTime,
                      amendedConfirmationDate,
                      amendedConfirmationTime,
                    });
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                  Done
                </button>
              </div>
            </>
          )}
            {amendStep === 3 && ( 
              <>
                <h3 className="text-lg font-semibold mb-2">Select Creditors to Serve</h3>
                <button
                  onClick={() =>
                    setAmendedCreditors(creditors2.map(c => ({ ...c, method: 'USM' })))
                  }
                  className="text-sm text-blue-600 mb-2"
                >
                  Select All as USM
                </button>
                <button
                  onClick={() => setAmendedCreditors([])}
                  className="ml-4 text-sm text-red-600 mb-2"
                >
                  Remove All
                </button>
                <div className="max-h-64 overflow-y-auto mb-4 border">
                  {creditors2.map((c, idx) => {
                    const selected = amendedCreditors.find(ac => (
                      ac.name === c.name
                      && ac.street1 === c.street1
                      && ac.zip_code === c.zip_code
                    ));
                    return (
                      <div key={idx} className="flex items-center justify-between p-2 border-b">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{c.name}</div>
                          <div className="text-sm text-gray-500 truncate">
                            {c.street1}{c.street2?.trim() ? `, ${c.street2}` : ''}, {c.city}, {c.state} {c.zip_code}
                          </div>
                        </div>
                        <select
                          value={selected?.method || ''}
                          onChange={e => {
                            const method = e.target.value;
                            setAmendedCreditors(prev => {
                              const matches = ac => (
                                ac.name === c.name
                                && ac.street1 === c.street1
                                && ac.zip_code === c.zip_code
                              );
                              if (!method) {
                                return prev.filter(ac => !matches(ac));
                              }
                              const exists = prev.find(matches);
                              if (exists) {
                                return prev.map(ac => matches(ac) ? { ...ac, method } : ac);
                              }
                              return [...prev, { ...c, method }];
                            });
                          }}
                          className="border rounded px-2 py-1"
                        >
                          <option value="">None</option>
                          <option value="USM">USM</option>
                          <option value="Certified">Certified</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between">
                  <button
                    onClick={() => setAmendStep(2)}
                    className="px-4 py-2 bg-gray-200 rounded"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      setShowAmendedModal(false);
                      setServiceRecipients(amendedCreditors.filter(c => c.method));
                      setData({
                        ...data,
                        amendedReason,
                        amendedCreditors,
                        amendedMeetingDate,
                        amendedMeetingTime,
                        amendedConfirmationDate,
                        amendedConfirmationTime,
                      });
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Button to generate SOFA data */}
                              <div className="flex items-center mb-4">
          <input
            type="checkbox"
            checked={data.amended}
            onChange={e => {
              const checked = e.target.checked;
              setData({ ...data, amended: checked });
              if (checked) {
                const scheduleDefaults = getAmendedPlanScheduleDefaults();
                setShowAmendedModal(true);
                setAmendStep(1);
                setAmendedMeetingDate(scheduleDefaults.meetingDate);
                setAmendedConfirmationDate(scheduleDefaults.confirmationDate);
                setAmendedMeetingTime(scheduleDefaults.meetingTime);
                setAmendedConfirmationTime('13:30');
              } else {
                setMailingMatrixService(null);
              }
            }}
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
          />
          <label className="ml-2 text-sm text-gray-700">Amended Filing</label>
        </div>
      <div className="flex justify-end mb-6">
        <button
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          onClick={() => setShowServiceModal(true)}
        >
          Generate Plan
        </button>
      </div>
      {showServiceModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-3">Select Service Recipients</h3>
            <p className="text-sm text-gray-600 mb-3">Choose how each creditor will be served for the Chapter 13 Plan.</p>
            {data.amended && (
              <MailingMatrixServiceSelector
                caseId={caseId}
                value={mailingMatrixService}
                onChange={(nextMatrixService) => {
                  setMailingMatrixService(nextMatrixService);
                  if (!nextMatrixService?.enabled) return;
                  const matrixMethod = nextMatrixService.method || 'USM';
                  const matrixRecipients = nextMatrixService.recipients || [];
                  setServiceRecipients(prev => prev.filter((selected) => {
                    const alreadyOnMatrix = matrixRecipients.some(matrixRecipient => (
                      matrixRecipient.name === selected.name
                      && matrixRecipient.street1 === selected.street1
                      && matrixRecipient.zip_code === selected.zip_code
                    ));
                    return !alreadyOnMatrix || selected.method !== matrixMethod;
                  }));
                }}
              />
            )}
            {mailingMatrixService?.enabled && (
              <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                The matrix is the base service list. Use the choices below only for creditors who need certified mail or another method different from the matrix-wide choice.
              </div>
            )}
            <div className="flex items-center gap-4 mb-3">
              {!mailingMatrixService?.enabled && (
                <button
                  onClick={() => setServiceRecipients(creditors2.map(c => ({ ...c, method: 'USM' })))}
                  className="text-sm text-blue-600"
                >
                  Select All as USM
                </button>
              )}
              <button
                onClick={() => setServiceRecipients([])}
                className="text-sm text-red-600"
              >
                {mailingMatrixService?.enabled ? 'Clear Additions / Overrides' : 'Clear All'}
              </button>
            </div>
            <div className="mb-2 text-sm font-medium text-gray-800">
              {mailingMatrixService?.enabled
                ? 'Additional creditors or service-method overrides'
                : 'Creditors to serve'}
            </div>
            <div className="max-h-72 overflow-y-auto border rounded">
              {creditors2.map((c, idx) => {
                const selected = serviceRecipients.find(ac => ac.name === c.name && ac.street1 === c.street1 && ac.zip_code === c.zip_code);
                return (
                  <div key={idx} className="flex items-center justify-between p-2 border-b">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {c.street1}{c.street2?.trim() ? `, ${c.street2}` : ''}, {c.city}, {c.state} {c.zip_code}
                      </div>
                    </div>
                    <select
                      value={selected?.method || ''}
                      onChange={(e) => {
                        const method = e.target.value;
                        setServiceRecipients(prev => {
                          const idx = prev.findIndex(ac => ac.name === c.name && ac.street1 === c.street1 && ac.zip_code === c.zip_code);
                          if (!method) {
                            return idx === -1
                              ? prev
                              : prev.filter((_recipient, recipientIndex) => recipientIndex !== idx);
                          }
                          if (idx !== -1) {
                            const next = [...prev];
                            next[idx] = { ...next[idx], method };
                            return next;
                          }
                          return [...prev, { ...c, method }];
                        });
                      }}
                      className="border rounded px-2 py-1 text-sm"
                    >
                      <option value="">None</option>
                      <option value="USM">USM</option>
                      <option value="Certified">Certified</option>
                    </select>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setShowServiceModal(false)}
                className="px-4 py-2 rounded border"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowServiceModal(false);
                  await handleGeneratePlan(serviceRecipients);
                }}
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Secured creditors */}
      <section className="mb-6 border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <div className="font-semibold">Secured Creditors (Plan Treatments)</div>
          <button className="text-sm inline-flex items-center gap-1" onClick={() => setAccordionOpen(a=>({...a, secured:!a.secured}))}>
            {accordionOpen.secured ? <>Hide <ChevronUp className="w-4 h-4"/></> : <>Show <ChevronDown className="w-4 h-4"/></>}
          </button>
        </header>
        {accordionOpen.secured && (
          <div>
            {secured.length === 0 && <div className="p-3 text-sm text-gray-500">No secured creditors.</div>}
            {secured.map(c => (
              <div key={c.id} className="p-3 border-b grid grid-cols-12 gap-4 text-sm items-start">
                {/* Header row */}
                <div className="col-span-12 md:col-span-3 min-w-0">
                  <div className="font-medium">{c.account_name}</div>
                  <div className="text-xs text-gray-500">Balance: ${toMoney(c.balance)}</div>
                  <div className="text-xs text-gray-500 break-words">Collateral: {linkedCollateralLabel(c)}</div>
                  <div className="text-xs text-gray-500">Collateral Value: ${toMoney(sumLinkedAssetValues(c))}</div>
                </div>
                <div className="col-span-12 md:col-span-3">
                  <label className="block text-xs font-medium mb-1">Treatment</label>
                  <select
                    className="w-full border rounded px-2 py-1.5"
                    value={c.chapter_13_secured_treatment || ""}
                    onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_secured_treatment: e.target.value })}
                  >
                    <option value="">Select…</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="cramdown">Cramdown</option>
                    <option value="910">910</option>
                    <option value="lien">Lien</option>
                    <option value="surrender">Surrender</option>
                    <option value="third">Third</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs font-medium mb-1">Direct Pay</label>
                  <input type="checkbox" checked={!!c.chapter_13_direct_pay} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_direct_pay: e.target.checked })}/>
                </div>
                <div className="col-span-6 md:col-span-4 flex items-center gap-2 md:justify-end">
                  <button
                    type="button"
                    className="text-xs px-3 py-1.5 h-9 rounded border hover:bg-gray-50"
                    onClick={() => openCreditorEditor(c)}
                  >Edit Creditor</button>
                  <button
                    type="button"
                    className="text-xs px-3 py-1.5 h-9 rounded border hover:bg-gray-50"
                    onClick={() => openAssetEditor(c)}
                  >Edit Asset(s)</button>
                  <button
                    className="text-xs px-3 py-1.5 h-9 rounded border hover:bg-gray-50"
                    onClick={async()=>{ try { await saveCreditor(c); alert("Saved"); } catch(e){ alert(e.message);} }}
                  >Save</button>
                </div>

                {/* Treatment fields row, full width */}
                {lc(c.chapter_13_secured_treatment) === "maintenance" && (
                  <div className="col-span-12 grid grid-cols-12 gap-3">
                    <div className="col-span-12 md:col-span-3">
                      <label className="block text-xs font-medium mb-1">Regular Pmt</label>
                      <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_maintenance_payment_amount ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_maintenance_payment_amount: e.target.value })}/>
                    </div>
                    <div className="col-span-12 md:col-span-3">
                      <label className="block text-xs font-medium mb-1">Arrearage</label>
                      <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.arrearage ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ arrearage: e.target.value, chapter_13_maintenance_arrearage_payment: toMoney(computeMonthlyPayment(e.target.value, c.chapter_13_maintenance_arrearage_interest_rate, c.chapter_13_maintenance_arrearage_months)) })}/>
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Int %</label>
                      <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_maintenance_arrearage_interest_rate ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_maintenance_arrearage_interest_rate: e.target.value, chapter_13_maintenance_arrearage_payment: toMoney(computeMonthlyPayment(c.arrearage, e.target.value, c.chapter_13_maintenance_arrearage_months)) })}/>
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Months</label>
                      <input type="number" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_maintenance_arrearage_months ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_maintenance_arrearage_months: e.target.value, chapter_13_maintenance_arrearage_payment: toMoney(computeMonthlyPayment(c.arrearage, c.chapter_13_maintenance_arrearage_interest_rate, e.target.value)) })}/>
                    </div>
                    <div className="col-span-12 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Arrearage Pmt (auto)</label>
                      <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_maintenance_arrearage_payment ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_maintenance_arrearage_payment: e.target.value })}/>
                    </div>
                  </div>
                )}
                {lc(c.chapter_13_secured_treatment) === 'third' && (
                  <div className="col-span-12 grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-12 md:col-span-8">
                      <label className="block text-xs font-medium mb-1">Third Party</label>
                      <input
                        type="text"
                        className="w-full h-9 border rounded px-2 py-1.5"
                        value={c.chapter_13_third_party ?? ''}
                        onChange={(e) => updateCreditorLocal(c.id, { chapter_13_third_party: e.target.value })}
                        placeholder="Enter third party name (e.g., Co‑signer, Finance Company)"
                      />
                    </div>
                  </div>
                )}
                {lc(c.chapter_13_secured_treatment) === "cramdown" && (
                  <div className="col-span-12 grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-12 md:col-span-3">
                      <label className="block text-xs font-medium mb-1">Secured Amt</label>
                      <div className="flex gap-2">
                        <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_cramdown_secured_amount ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_cramdown_secured_amount: e.target.value, chapter_13_cramdown_payment: toMoney(computeMonthlyPayment(e.target.value, c.chapter_13_cramdown_interest_rate, c.chapter_13_cramdown_months)) })}/>
                        <button
                          type="button"
                          className="px-2 py-1.5 h-9 text-xs border rounded hover:bg-gray-50"
                          onClick={() => {
                            const assetSum = sumLinkedAssetValues(c);
                            const secured = clamp(assetSum, 0, Number(c.balance) || 0);
                            updateCreditorLocal(c.id, {
                              chapter_13_cramdown_secured_amount: toMoney(secured),
                              chapter_13_cramdown_payment: toMoney(computeMonthlyPayment(secured, c.chapter_13_cramdown_interest_rate, c.chapter_13_cramdown_months))
                            });
                          }}
                        >
                          Use Asset Value
                        </button>
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Int %</label>
                      <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_cramdown_interest_rate ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_cramdown_interest_rate: e.target.value, chapter_13_cramdown_payment: toMoney(computeMonthlyPayment(c.chapter_13_cramdown_secured_amount, e.target.value, c.chapter_13_cramdown_months)) })}/>
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Months</label>
                      <input type="number" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_cramdown_months ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_cramdown_months: e.target.value, chapter_13_cramdown_payment: toMoney(computeMonthlyPayment(c.chapter_13_cramdown_secured_amount, c.chapter_13_cramdown_interest_rate, e.target.value)) })}/>
                    </div>
                    <div className="col-span-12 md:col-span-3">
                      <label className="block text-xs font-medium mb-1">Asset Value (linked)</label>
                      <input
                        type="number" step="0.01" readOnly
                        className="w-full h-9 border rounded px-2 py-1.5 text-right bg-gray-50"
                        value={toMoney(sumLinkedAssetValues(c))}
                      />
                    </div>
                    <div className="col-span-12 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Payment (auto)</label>
                      <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_cramdown_payment ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_cramdown_payment: e.target.value })}/>
                    </div>
                  </div>
                )}

                {lc(c.chapter_13_secured_treatment) === "910" && (
                  <div className="col-span-12 grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-12 md:col-span-3">
                      <label className="block text-xs font-medium mb-1">Secured Amt</label>
                      <div className="flex gap-2">
                        <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_910_secured_amount ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_910_secured_amount: e.target.value, chapter_13_910_payment: toMoney(computeMonthlyPayment(e.target.value, c.chapter_13_910_interest_rate, c.chapter_13_910_months)) })}/>
                        <button
                          type="button"
                          className="px-2 py-1.5 h-9 text-xs border rounded hover:bg-gray-50"
                          onClick={() => {
                            const claim = clamp(Number(c.balance) || 0, 0, Number(c.balance) || 0);
                            updateCreditorLocal(c.id, {
                              chapter_13_910_secured_amount: toMoney(claim),
                              chapter_13_910_payment: toMoney(computeMonthlyPayment(claim, c.chapter_13_910_interest_rate, c.chapter_13_910_months))
                            });
                          }}
                        >
                          Use Claim Amount
                        </button>
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Int %</label>
                      <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_910_interest_rate ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_910_interest_rate: e.target.value, chapter_13_910_payment: toMoney(computeMonthlyPayment(c.chapter_13_910_secured_amount, e.target.value, c.chapter_13_910_months)) })}/>
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Months</label>
                      <input type="number" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_910_months ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_910_months: e.target.value, chapter_13_910_payment: toMoney(computeMonthlyPayment(c.chapter_13_910_secured_amount, c.chapter_13_910_interest_rate, e.target.value)) })}/>
                    </div>
                    <div className="col-span-12 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Payment (auto)</label>
                      <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_910_payment ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_910_payment: e.target.value })}/>
                    </div>
                  </div>
                )}

                {lc(c.chapter_13_secured_treatment) === "lien" && (
                  <div className="col-span-12 grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-12 md:col-span-3">
                      <label className="block text-xs font-medium mb-1">Secured Amt</label>
                      <div className="flex gap-2">
                        <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_lien_secured_amount ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_lien_secured_amount: e.target.value, chapter_13_lien_payment: toMoney(computeMonthlyPayment(e.target.value, c.chapter_13_lien_interest_rate, c.chapter_13_lien_months)) })}/>
                        <button
                          type="button"
                          className="px-2 py-1.5 h-9 text-xs border rounded hover:bg-gray-50"
                          onClick={() => {
                            const assetSum = sumLinkedAssetValues(c);
                            const other = otherLiensTotal(c, secured); // consider only secured liens among loaded secured creditors
                            const exemption = sumLinkedExemptions(c);
                            const preliminary = assetSum - other - exemption;
                            const securedAmt = clamp(preliminary, 0, Number(c.balance) || 0);
                            updateCreditorLocal(c.id, {
                              chapter_13_lien_other_lien_amount: toMoney(other),
                              chapter_13_lien_secured_amount: toMoney(securedAmt),
                              chapter_13_lien_payment: toMoney(computeMonthlyPayment(securedAmt, c.chapter_13_lien_interest_rate, c.chapter_13_lien_months))
                            });
                          }}
                        >
                          Auto Compute
                        </button>
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Int %</label>
                      <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_lien_interest_rate ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_lien_interest_rate: e.target.value, chapter_13_lien_payment: toMoney(computeMonthlyPayment(c.chapter_13_lien_secured_amount, e.target.value, c.chapter_13_lien_months)) })}/>
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Months</label>
                      <input type="number" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_lien_months ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_lien_months: e.target.value, chapter_13_lien_payment: toMoney(computeMonthlyPayment(c.chapter_13_lien_secured_amount, c.chapter_13_lien_interest_rate, e.target.value)) })}/>
                    </div>
                    <div className="col-span-12 md:col-span-3">
                      <label className="block text-xs font-medium mb-1">Asset Value (linked)</label>
                      <input
                        type="number" step="0.01" readOnly
                        className="w-full h-9 border rounded px-2 py-1.5 text-right bg-gray-50"
                        value={toMoney(sumLinkedAssetValues(c))}
                      />
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Other Liens</label>
                      <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_lien_other_lien_amount ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_lien_other_lien_amount: e.target.value })}/>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <label className="block text-xs font-medium mb-1">Exemptions (linked)</label>
                      <input
                        type="number" step="0.01" readOnly
                        className="w-full h-9 border rounded px-2 py-1.5 text-right bg-gray-50"
                        value={toMoney(sumLinkedExemptions(c))}
                      />
                    </div>
                    <div className="col-span-12 md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Payment (auto)</label>
                      <input type="number" step="0.01" className="w-full h-9 border rounded px-2 py-1.5 text-right" value={c.chapter_13_lien_payment ?? ''} onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_lien_payment: e.target.value })}/>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Executory contracts */}
      <section className="mb-6 border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <div className="font-semibold">Executory Contracts (Assumption / Payments)</div>
          <button className="text-sm inline-flex items-center gap-1" onClick={() => setAccordionOpen(a=>({...a, executory:!a.executory}))}>
            {accordionOpen.executory ? <>Hide <ChevronUp className="w-4 h-4"/></> : <>Show <ChevronDown className="w-4 h-4"/></>}
          </button>
        </header>
       {accordionOpen.executory && (
          <div>
            {executory.length === 0 && <div className="p-3 text-sm text-gray-500">No executory contracts.</div>}
            {executory.map(c => (
              <div key={c.id} className="p-3 border-b grid grid-cols-12 gap-4 text-sm items-start">
                <div className="col-span-12 md:col-span-4 min-w-0">
                  <div className="font-medium">{c.account_name}</div>
                  <div className="text-xs text-gray-500">Lease/Contract</div>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs font-medium mb-1">Assume?</label>
                  <select className="w-full border rounded px-2 py-1.5" value={c.chapter_13_assumption ? "true":"false"}
                    onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_assumption: e.target.value === "true" })}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
                {/* Direct Pay checkbox for executory contracts */}
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs font-medium mb-1">Direct Pay?</label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!c.chapter_13_direct_pay}
                      onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_direct_pay: e.target.checked })}
                    />
                    <span className="text-xs text-gray-700">Debtors pay directly</span>
                  </label>
                </div>
                <div className="col-span-6 md:col-span-4 flex items-center gap-2 md:justify-end">
                  <button
                    type="button"
                    className="text-xs px-3 py-1.5 h-9 rounded border hover:bg-gray-50"
                    onClick={() => openCreditorEditor(c)}
                  >Edit Creditor</button>
                  <button
                    type="button"
                    className="text-xs px-3 py-1.5 h-9 rounded border hover:bg-gray-50"
                    onClick={() => openAssetEditor(c)}
                  >Edit Asset(s)</button>
                  <button className="text-xs px-3 py-1.5 h-9 rounded border hover:bg-gray-50" onClick={async()=>{ try { await saveCreditor(c); alert('Saved'); } catch(e){ alert(e.message);} }}>Save</button>
                </div>
                <div className="col-span-12 grid grid-cols-12 gap-3">
                  <div className="col-span-12 md:col-span-3">
                    <label className="block text-xs font-medium mb-1">Regular Pmt</label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full h-9 border rounded px-2 py-1.5 text-right"
                      value={c.chapter_13_maintenance_payment_amount ?? ''}
                      onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_maintenance_payment_amount: e.target.value })}
                    />
                  </div>

                  <div className="col-span-12 md:col-span-3">
                    <label className="block text-xs font-medium mb-1">Arrearage Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full h-9 border rounded px-2 py-1.5 text-right"
                      value={c.arrearage ?? ''}
                      onChange={(e)=>updateCreditorLocal(
                        c.id,
                        {
                          arrearage: e.target.value,
                          chapter_13_maintenance_arrearage_payment: toMoney(
                            computeMonthlyPayment(e.target.value, c.chapter_13_maintenance_arrearage_interest_rate, c.chapter_13_maintenance_arrearage_months)
                          )
                        }
                      )}
                    />
                  </div>

                  <div className="col-span-6 md:col-span-2">
                    <label className="block text-xs font-medium mb-1">Arrears Int %</label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full h-9 border rounded px-2 py-1.5 text-right"
                      value={c.chapter_13_maintenance_arrearage_interest_rate ?? ''}
                      onChange={(e)=>updateCreditorLocal(
                        c.id,
                        {
                          chapter_13_maintenance_arrearage_interest_rate: e.target.value,
                          chapter_13_maintenance_arrearage_payment: toMoney(
                            computeMonthlyPayment(c.arrearage, e.target.value, c.chapter_13_maintenance_arrearage_months)
                          )
                        }
                      )}
                    />
                  </div>

                  <div className="col-span-6 md:col-span-2">
                    <label className="block text-xs font-medium mb-1">Arrears Months</label>
                    <input
                      type="number"
                      className="w-full h-9 border rounded px-2 py-1.5 text-right"
                      value={c.chapter_13_maintenance_arrearage_months ?? ''}
                      onChange={(e)=>updateCreditorLocal(
                        c.id,
                        {
                          chapter_13_maintenance_arrearage_months: e.target.value,
                          chapter_13_maintenance_arrearage_payment: toMoney(
                            computeMonthlyPayment(c.arrearage, c.chapter_13_maintenance_arrearage_interest_rate, e.target.value)
                          )
                        }
                      )}
                    />
                  </div>

                  <div className="col-span-12 md:col-span-2">
                    <label className="block text-xs font-medium mb-1">Arrears Pmt (auto)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full h-9 border rounded px-2 py-1.5 text-right"
                      value={c.chapter_13_maintenance_arrearage_payment ?? ''}
                      onChange={(e)=>updateCreditorLocal(c.id,{ chapter_13_maintenance_arrearage_payment: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Plan settings */}
      <section className="mb-6 border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <div className="font-semibold">Plan Funding</div>
          <button className="text-sm inline-flex items-center gap-1" onClick={() => setAccordionOpen(a=>({...a, settings:!a.settings}))}>
            {accordionOpen.settings ? <>Hide <ChevronUp className="w-4 h-4"/></> : <>Show <ChevronDown className="w-4 h-4"/></>}
          </button>
        </header>
        {accordionOpen.settings && plan && (
          <div className="p-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1">Debt figures used by calculator</label>
              <select
                className="w-full border rounded px-2 py-1.5"
                value={plan.debt_calculation_source || "scheduled"}
                onChange={(e) => setPlan({ ...plan, debt_calculation_source: e.target.value })}
              >
                <option value="scheduled">Scheduled creditors</option>
                <option value="claims">Filed proofs of claim</option>
                <option value="hybrid">Hybrid: claims replace matched scheduled debts</option>
              </select>
              <div className="mt-1 text-[11px] text-gray-600">
                {claimsSummary.includedCount} active claim{claimsSummary.includedCount === 1 ? "" : "s"}
                {` • $${Number(claimsSummary.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} filed • ${claimsSummary.unmatchedCount} unmatched • ${claimsSummary.warningCount} review warning${claimsSummary.warningCount === 1 ? "" : "s"}`}
              </div>
              <button
                type="button"
                onClick={() => navigate(`/case/${caseId}/creditors`)}
                className="mt-1 text-xs font-medium text-indigo-700 hover:underline"
              >
                Review and reconcile filed claims
              </button>
              {claimsLoadError && <div className="mt-1 text-xs text-red-600">{claimsLoadError}</div>}
              {(plan.debt_calculation_source || "scheduled") !== "scheduled" && claimsSummary.includedCount === 0 && (
                <div className="mt-1 text-xs text-amber-700">No active filed claims are available. Review the Proof of Claim section on the Creditors page.</div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Plan Length (months)</label>
              <input type="number" className="w-full border rounded px-2 py-1.5 text-right" value={plan.plan_length ?? ''} onChange={(e)=>setPlan({ ...plan, plan_length: e.target.value })}/>
            </div>
            <div className="md:col-span-4">
              <label className="block text-xs font-medium mb-1">DSO Treatment</label>
              <input type="text" className="w-full border rounded px-2 py-1.5" value={plan.dso_treatment || ''} onChange={(e)=>setPlan({ ...plan, dso_treatment: e.target.value })}/>
            </div>
     

            <div className="md:col-span-4 grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-1 font-semibold text-gray-700">Debtor 1 Payments</div>
              <div>
                <label className="block text-xs font-medium mb-1">Amount</label>
                <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={plan.payment_debtor_1 ?? ''} onChange={(e)=>setPlan({ ...plan, payment_debtor_1: e.target.value })}/>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Period</label>
                <select className="w-full border rounded px-2 py-1.5" value={plan.payment_period_debtor_1 || ''} onChange={(e)=>setPlan({ ...plan, payment_period_debtor_1: e.target.value })}>
                  <option value="">—</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="semi-monthly">Semi-Monthly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Source</label>
                <select className="w-full border rounded px-2 py-1.5" value={plan.payment_source_debtor_1 || ''} onChange={(e)=>setPlan({ ...plan, payment_source_debtor_1: e.target.value })}>
                  <option value="">—</option>
                  <option value="direct">Direct</option>
                  <option value="payroll">Payroll</option>
                </select>
              </div>
            </div>
            <FuturePaymentChangesEditor
              debtorNumber={1}
              changes={futurePaymentChangesForDebtor(1)}
              onAdd={addFuturePaymentChange}
              onChange={updateFuturePaymentChange}
              onRemove={removeFuturePaymentChange}
            />

            <div className="md:col-span-4 grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-1 font-semibold text-gray-700">Debtor 2 Payments</div>
              <div>
                <label className="block text-xs font-medium mb-1">Amount</label>
                <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={plan.payment_debtor_2 ?? ''} onChange={(e)=>setPlan({ ...plan, payment_debtor_2: e.target.value })}/>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Period</label>
                <select className="w-full border rounded px-2 py-1.5" value={plan.payment_period_debtor_2 || ''} onChange={(e)=>setPlan({ ...plan, payment_period_debtor_2: e.target.value })}>
                  <option value="">—</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="semi-monthly">Semi-Monthly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Source</label>
                <select className="w-full border rounded px-2 py-1.5" value={plan.payment_source_debtor_2 || ''} onChange={(e)=>setPlan({ ...plan, payment_source_debtor_2: e.target.value })}>
                  <option value="">—</option>
                  <option value="direct">Direct</option>
                  <option value="payroll">Payroll</option>
                </select>
              </div>
            </div>
            <FuturePaymentChangesEditor
              debtorNumber={2}
              changes={futurePaymentChangesForDebtor(2)}
              onAdd={addFuturePaymentChange}
              onChange={updateFuturePaymentChange}
              onRemove={removeFuturePaymentChange}
            />
            <div className="md:col-span-4 p-3 border rounded">
              <div className="font-semibold text-gray-700 mb-2">Auto-calc Debtor Payments</div>
              <div className="text-xs text-gray-600 mb-2">Distribute the estimated monthly plan payment between Debtor 1 and Debtor 2 based on this split, then convert to each debtor's selected period.</div>
              <div className="flex items-center gap-3">
                <span className="text-xs">D1 {splitPct}%</span>
                <input type="range" min={0} max={100} value={splitPct} onChange={(e)=>setSplitPct(Number(e.target.value))} className="flex-1"/>
                <span className="text-xs">D2 {100 - splitPct}%</span>
              </div>
              <div className="mt-2 text-xs text-gray-600">Current monthly target: ${toMoney(totals.planPayment)} • D1 monthly share: ${toMoney(totals.planPayment * (splitPct/100))} • D2 monthly share: ${toMoney(totals.planPayment * (1 - splitPct/100))}</div>
              <div className="mt-2">
                <button type="button" className="px-3 py-1.5 text-xs border rounded hover:bg-gray-50" onClick={autoCalcDebtorPayments}>Apply Auto-calc</button>
              </div>
            </div>
            {/* End Plan Settings section */}
          </div>
        )}
      </section>

 {/* Additional Payments section */}
      <section className="mb-6 border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <div className="font-semibold">Additional Payments</div>
          <div className="text-xs text-gray-500">(one-off or extra plan payments)</div>
        </header>
        <div className="p-3 text-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-2 border text-left">Source</th>
                  <th className="p-2 border text-right">Amount</th>
                  <th className="p-2 border text-left">Date</th>
                  <th className="p-2 border text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {additionalPays.map(ap => (
                  <tr key={ap.id}>
                    <td className="p-2 border"><input className="w-full" value={ap.source||''} onChange={(e)=>setAdditionalPays(prev=>prev.map(x=>x.id===ap.id?{...x, source:e.target.value}:x))}/></td>
                    <td className="p-2 border text-right"><input type="number" step="0.01" className="w-full text-right" value={ap.amount ?? ''} onChange={(e)=>setAdditionalPays(prev=>prev.map(x=>x.id===ap.id?{...x, amount:e.target.value}:x))}/></td>
                    <td className="p-2 border"><input type="date" className="w-full" value={ap.date || ''} onChange={(e)=>setAdditionalPays(prev=>prev.map(x=>x.id===ap.id?{...x, date:e.target.value}:x))}/></td>
                    <td className="p-2 border text-center space-x-2">
                      <button className="px-2 py-1 border rounded" onClick={async()=>{ try{ const saved = await updateAdditionalPayment(plan.id, ap.id, ap); setAdditionalPays(prev=>prev.map(x=>x.id===ap.id?saved:x)); }catch(e){ alert(e.message);} }}>Save</button>
                      <button className="px-2 py-1 border rounded" onClick={async()=>{ if(!window.confirm('Delete additional payment?')) return; try{ await deleteAdditionalPayment(plan.id, ap.id); setAdditionalPays(prev=>prev.filter(x=>x.id!==ap.id)); }catch(e){ alert(e.message);} }}>Delete</button>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="p-2 border"><input id="ap_new_source" className="w-full" placeholder="e.g., Net Bonus"/></td>
                  <td className="p-2 border text-right"><input id="ap_new_amount" type="number" step="0.01" className="w-full text-right" placeholder="0.00"/></td>
                  <td className="p-2 border"><input id="ap_new_date" type="date" className="w-full"/></td>
                  <td className="p-2 border text-center">
                    <button className="px-3 py-1.5 text-xs border rounded" onClick={async()=>{
                      const source = document.getElementById('ap_new_source').value;
                      const amount = document.getElementById('ap_new_amount').value;
                      const date = document.getElementById('ap_new_date').value;
                      try{
                        const created = await createAdditionalPayment(plan.id, { source, amount, date });
                        setAdditionalPays(prev=>[...prev, created]);
                        document.getElementById('ap_new_source').value='';
                        document.getElementById('ap_new_amount').value='';
                        document.getElementById('ap_new_date').value='';
                      }catch(e){ alert(e.message); }
                    }}>Add</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
      {/* Tax Refunds section */}
      <section className="mb-6 border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <div className="font-semibold">Tax Refunds</div>
          <div className="text-xs text-gray-500">Trustee treatment of tax refunds</div>
        </header>
        <div className="p-3 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium mb-1">Refund Payment</label>
              <select className="w-full border rounded px-2 py-1.5"
                value={plan?.refund_payment || 'all'}
                onChange={(e)=>setPlan(prev=>({...prev, refund_payment: e.target.value}))}
              >
                <option value="all">All</option>
                <option value="none">None</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className={`md:col-span-2 ${(plan?.refund_payment === 'other') ? '' : 'opacity-60 pointer-events-none'}`}>
              <label className="block text-xs font-medium mb-1">If Other, describe</label>
              <textarea aria-label="Tax refund treatment description" className="w-full min-h-[120px] border rounded px-2 py-1.5" placeholder="Describe refund treatment"
                value={plan?.refund_payment_other_description || ''}
                onChange={(e)=>setPlan(prev=>({...prev, refund_payment_other_description: e.target.value}))}
              />
              {plan?.refund_payment === 'other' && (
  <div className="mt-3 border rounded p-3">
    <div className="text-xs font-semibold mb-2">Quick Fill Templates</div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
      <div className="md:col-span-2 flex flex-wrap gap-2">
        <TaxRefundTemplateControls
          onApply={description => setPlan(previous => ({
            ...(previous || {}), refund_payment_other_description: description,
          }))}
        />

        <button
          type="button"
          className="px-3 py-1.5 text-xs border rounded hover:bg-gray-50"
          onClick={() => {
            const amt = Number(householdSize || 0) * 500;
            const txt = `In excess of $${amt.toFixed(2)}`;
            setPlan(prev => ({ ...(prev || {}), refund_payment_other_description: txt }));
          }}
        >
          Use $500 × Household Size
        </button>
      </div>

      <div>
        <label className="block text-xs font-medium mb-1">Household Size</label>
        <input
          type="number"
          min={1}
          className="w-full border rounded px-2 py-1.5 text-right"
          value={householdSize}
          onChange={e => setHouseholdSize(Math.max(1, Number(e.target.value) || 1))}
        />
        <div className="text-[10px] text-gray-500 mt-1">
          $500 × size = ${ (Number(householdSize || 0) * 500).toFixed(2) }
        </div>
      </div>
    </div>
  </div>
)}
            </div>
          </div>
        </div>
      </section>

 {/* Unsecured Treatment */}
      <section className="mb-6 border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <div className="font-semibold">Unsecured Treatment</div>
        </header>
        {plan && (
          <div className="p-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div>
              <label className="block text-xs font-medium mb-1">Unsecured Treatment</label>
              <select className="w-full border rounded px-2 py-1.5" value={plan.unsecured_treatment || ''} onChange={(e)=>setPlan({ ...plan, unsecured_treatment: e.target.value })}>
                <option value="">Select…</option>
                <option value="full">Full</option>
                <option value="percent">Percent</option>
                <option value="pro rata">Pro Rata</option>
                <option value="bic">Best Interest</option>
                <option value="pdi">PDI</option>
                <option value="other">Other</option>
              </select>
            </div>

            {plan.unsecured_treatment === 'percent' && (
              <div>
                <label className="block text-xs font-medium mb-1">Unsecured %</label>
                <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={plan.unsecured_percentage ?? ''} onChange={(e)=>setPlan({ ...plan, unsecured_percentage: e.target.value })}/>
              </div>
            )}

            {plan.unsecured_treatment === 'bic' && (
              <div>
                <label className="block text-xs font-medium mb-1">Best Interest Amount</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    className="w-full border rounded px-2 py-1.5 text-right"
                    value={plan.best_interest_amount ?? ''}
                    onChange={(e)=>setPlan({ ...plan, best_interest_amount: e.target.value })}
                  />
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs border rounded hover:bg-gray-50 whitespace-nowrap"
                    onClick={() => {
                      const bic = computeBestInterestAmount(creditors, caseAssets);
                      setPlan(prev => ({ ...(prev||{}), best_interest_amount: toMoney(bic) }));
                    }}
                  >Auto-calc</button>
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">Liquidation analysis over all assets</div>
              </div>
            )}

            {plan.unsecured_treatment === 'pdi' && (
              <div>
                <label className="block text-xs font-medium mb-1">PDI Amount</label>
                <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={plan.pdi_amount ?? ''} onChange={(e)=>setPlan({ ...plan, pdi_amount: e.target.value })}/>
              </div>
            )}

            {plan.unsecured_treatment === 'other' && (
              <div className="md:col-span-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Left: amounts */}
                <div>
                  <label className="block text-xs font-medium mb-1">Other Amount</label>
                  <input
                    aria-label="Other Amount"
                    type="number"
                    step="0.01"
                    className="w-full border rounded px-2 py-1.5 text-right"
                    value={plan.other_amount ?? ''}
                    onChange={(e)=>setPlan({ ...plan, other_amount: e.target.value })}
                  />
                </div>

                {/* Middle: larger description area */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium mb-1">Other Description</label>
                  <textarea
                    aria-label="Other Description"
                    className="w-full border rounded px-2 py-1.5 min-h-[96px]"
                    value={plan.unsecured_other_description || ''}
                    onChange={(e)=>setPlan({ ...plan, unsecured_other_description: e.target.value })}
                    placeholder="Describe how unsecureds are treated…"
                  />
                  <div className="text-[10px] text-gray-500 mt-1">
                    {(plan.unsecured_other_description || '').length.toLocaleString()} characters
                  </div>
                  {/* Quick Fill Templates */}
                  <div className="mt-3 border rounded p-3">
                    <div className="text-xs font-semibold mb-2">Quick Fill Templates</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                      <div className="md:col-span-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="px-3 py-1.5 text-xs border rounded hover:bg-gray-50"
                          onClick={() => {
                            const amt = plan.other_amount ?? 0;
                            const txt = `To meet the Projected Disposable Income requirements of 11 U.S.C. § 1325(b)(1)(B), the total amount paid to unsecured creditors must be at least $${toTemplateMoney(amt)} unless their claims are paid in full.`;
                            setPlan(prev => ({
                              ...(prev || {}),
                              unsecured_other_description: txt,
                            }));
                          }}
                        >
                          Use PDI Template
                        </button>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Template Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full border rounded px-2 py-1.5 text-right"
                          value={plan.other_amount ?? ''}
                          onChange={e =>
                            setPlan(prev => ({
                              ...(prev || {}),
                              other_amount: parseFloat(e.target.value) || 0,
                            }))
                          }
                        />
                        <div className="text-[10px] text-gray-500 mt-1">Used by the PDI quick-fill template.</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <BestInterestTemplateControls
                      initialNonJointAmount={plan.other_amount ?? ''}
                      onApply={applyBestInterestTemplate}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>


      {/* Post-Petition Claims */}
      <section className="mb-6 border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <div className="font-semibold">Post-Petition Claims</div>
          <div className="text-xs text-gray-500">Treatment of allowed post-petition tax and consumer claims</div>
        </header>
        <div className="p-3 text-sm space-y-4">
          {/* Tax Claims: Full or None (mutually exclusive) */}
          <div className="border rounded p-3">
            <div className="font-medium mb-2">Tax Claims</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="tax-claims-choice"
                  checked={!!plan?.tax_claims_full}
                  onChange={() => setPlan(prev => ({
                    ...prev,
                    tax_claims_full: true,
                    tax_claims_none: false,
                  }))}
                />
                <span>Paid in Full by Trustee</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="tax-claims-choice"
                  checked={!!plan?.tax_claims_none}
                  onChange={() => setPlan(prev => ({
                    ...prev,
                    tax_claims_full: false,
                    tax_claims_none: true,
                  }))}
                />
                <span>Not Paid by Trustee</span>
              </label>
            </div>
          </div>

          {/* Consumer Claims: Full / None / Per Unsecured Treatment (mutually exclusive) */}
          <div className="border rounded p-3">
            <div className="font-medium mb-2">Consumer Claims</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="consumer-claims-choice"
                  checked={!!plan?.consumer_claims_full}
                  onChange={() => setPlan(prev => ({
                    ...prev,
                    consumer_claims_full: true,
                    consumer_claims_none: false,
                    consumer_claims_per_unsecured_treatment: false,
                  }))}
                />
                <span>Paid in Full by Trustee</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="consumer-claims-choice"
                  checked={!!plan?.consumer_claims_none}
                  onChange={() => setPlan(prev => ({
                    ...prev,
                    consumer_claims_full: false,
                    consumer_claims_none: true,
                    consumer_claims_per_unsecured_treatment: false,
                  }))}
                />
                <span>Not Paid by Trustee</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="consumer-claims-choice"
                  checked={!!plan?.consumer_claims_per_unsecured_treatment}
                  onChange={() => setPlan(prev => ({
                    ...prev,
                    consumer_claims_full: false,
                    consumer_claims_none: false,
                    consumer_claims_per_unsecured_treatment: true,
                  }))}
                />
                <span>Per Unsecured Treatment</span>
              </label>
            </div>
          </div>

        </div>
      </section>
     

      {/* Admin Costs section */}
      <section className="mb-6 border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <div className="font-semibold">Admin Costs</div>
          <div className="text-xs text-gray-500">Attorney fees, trustee %, and filing fees</div>
        </header>
        <div className="p-3 text-sm">
          <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium mb-1">Attorney Fees (total)</label>
              <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={attorneyTotal} onChange={(e)=>setAttorneyTotal(e.target.value)} />
              <div className="text-[10px] text-gray-500 mt-0.5">Saved on case (Overview)</div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Attorney Fees Paid</label>
              <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={attorneyPaid} onChange={(e)=>setAttorneyPaid(e.target.value)} />
              <div className="text-[10px] text-gray-500 mt-0.5">Saved on case (Overview)</div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Trustee %</label>
              <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={trusteePct} onChange={(e)=>setTrusteePct(e.target.value)} />
              <div className="text-[10px] text-gray-500 mt-0.5">Saved on case (Overview)</div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Filing Fee (total)</label>
              <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={filingFeeTotal} onChange={(e)=>setFilingFeeTotal(e.target.value)} />
              <div className="text-[10px] text-gray-500 mt-0.5">Saved on case (Overview)</div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Filing Fee Paid</label>
              <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={filingFeePaid} onChange={(e)=>setFilingFeePaid(e.target.value)} />
              <div className="text-[10px] text-gray-500 mt-0.5">Saved on case (Overview)</div>
            </div>
            <div className="flex items-center gap-2">
              <input id="incldp" type="checkbox" checked={includeDirectPay} onChange={(e)=>setIncludeDirectPay(e.target.checked)} />
              <label htmlFor="incldp" className="text-xs">Include Direct-Pay items in calc</label>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator output */}
      <section className="mb-10 border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <div className="flex items-center gap-4">
            <div className="font-semibold">Plan Payment Calculator</div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={includeStudentLoans}
                onChange={(e)=>setIncludeStudentLoans(e.target.checked)}
              />
              <span>Include Student Loans in Unsecured</span>
            </label>
          </div>
          <button className="text-sm inline-flex items-center gap-1" onClick={() => setAccordionOpen(a=>({...a, calc:!a.calc}))}>
            {accordionOpen.calc ? <>Hide <ChevronUp className="w-4 h-4"/></> : <>Show <ChevronDown className="w-4 h-4"/></>}
          </button>
        </header>
        {accordionOpen.calc && (
          <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="p-3 border rounded">
              <div className="font-semibold mb-2">Monthly Components</div>
              <div className="flex justify-between"><span>Maintenance (incl. arrears)</span><span>${toMoney(totals.sumMaint)}</span></div>
              <div className="flex justify-between"><span>Cramdown</span><span>${toMoney(totals.sumCram)}</span></div>
              <div className="flex justify-between"><span>910</span><span>${toMoney(totals.sum910)}</span></div>
              <div className="flex justify-between"><span>Lien Strip</span><span>${toMoney(totals.sumLien)}</span></div>
              <div className="flex justify-between"><span>Executory</span><span>${toMoney(totals.sumExec)}</span></div>
              <div className="flex justify-between"><span>Priority (paid in full)</span><span>${toMoney(totals.monthlyPriorityFull)}</span></div>
              <div className="flex justify-between"><span>Unsecured (per treatment)</span><span>${toMoney(totals.monthlyUnsecured)}</span></div>
            </div>
            <div className="p-3 border rounded">
              <div className="font-semibold mb-2">Overhead</div>
              <div className="flex justify-between"><span>Attorney Fees Remaining / mo</span><span>${toMoney(totals.attorneyRemaining / Math.max(1, numOr(plan?.plan_length,1)))}</span></div>
              <div className="flex justify-between"><span>Filing Fees Remaining / mo</span><span>${toMoney(totals.filingRemaining / Math.max(1, numOr(plan?.plan_length,1)))}</span></div>
              <div className="flex justify-between"><span>Trustee Fee (monthly)</span><span>${toMoney(totals.trusteeFee)}</span></div>
            </div>
            <div className="p-3 border rounded bg-indigo-50">
              <div className="font-semibold mb-2">Estimated Required Plan Payment</div>
              <div className="text-3xl font-bold">${toMoney(totals.planPayment)}</div>
              <div className="text-xs text-gray-600 mt-2">This is an estimate. Actual application of trustee fees and step payments may vary.</div>
            </div>
          </div>
        )}
        {/* Unsecured Base Breakdown (debug/trace) */}
        {accordionOpen.calc && (
          <div className="p-3 border rounded mt-4">
            <div className="font-semibold mb-2">Unsecured Base Breakdown</div>
            <div className="text-xs text-gray-600 space-y-1">
              <div className="flex justify-between"><span>Undersecured (cramdown/lien)</span><span>${toMoney(totals.undersecuredFromSecured)}</span></div>
              <div className="flex justify-between"><span>Nonpriority part of priority</span><span>${toMoney(totals.nonPriorityPart)}</span></div>
              <div className="flex justify-between"><span>Total Unsecured Base</span><span>${toMoney(totals.unsecuredBase)}</span></div>
              {lc(plan?.unsecured_treatment||'')==='bic' && (
                <div className="flex justify-between"><span>Best Interest (input)</span><span>${toMoney(plan?.best_interest_amount)}</span></div>
              )}
            </div>
          </div>
        )}
        {/* Best Interest (Liquidation) Breakdown */}
        {accordionOpen.calc && plan?.unsecured_treatment === 'bic' && (
          <section className="p-3 border rounded mt-4">
            <div className="font-semibold mb-2">Best Interest (Liquidation) Breakdown</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 border text-left">Asset</th>
                    <th className="p-2 border text-right">Base Value</th>
                    <th className="p-2 border text-right">Sale Fee (6%)</th>
                    <th className="p-2 border text-right">Exemption</th>
                    <th className="p-2 border text-right">Linked Liens</th>
                    <th className="p-2 border text-right">Ch. 7 Fee</th>
                    <th className="p-2 border text-right">Liquidation</th>
                    <th className="p-2 border text-center">Counts?</th>
                  </tr>
                </thead>
                <tbody>
                  {bicBreakdown.rows.map(r => (
                    <tr key={r.key}>
                      <td className="p-2 border">{r.label}</td>
                      <td className="p-2 border text-right">${toMoney(r.baseValue)}</td>
                      <td className="p-2 border text-right">${toMoney(r.saleFee)}</td>
                      <td className="p-2 border text-right">${toMoney(r.exemption)}</td>
                      <td className="p-2 border text-right">${toMoney(r.creditorPortion)}</td>
                      <td className="p-2 border text-right">${toMoney(r.ch7Fee)}</td>
                      <td className={`p-2 border text-right ${r.liquidation > 0 ? 'text-green-700' : 'text-gray-500'}`}>${toMoney(r.liquidation)}</td>
                      <td className="p-2 border text-center">{r.contributes ? '✓' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-indigo-50 font-semibold">
                    <td className="p-2 border text-right" colSpan={6}>Total Positive Liquidation</td>
                    <td className="p-2 border text-right">${toMoney(bicBreakdown.total)}</td>
                    <td className="p-2 border text-center">BIC</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="text-[10px] text-gray-500 mt-1">For real property/vehicles, sale fee &amp; Ch. 7 fee are applied to total value; creditor balances are prorated by ownership.</div>
          </section>
        )}
      </section>
      <section className="mb-6 border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <div className="font-semibold">Non-Standard Provisions</div>
          <div className="text-xs text-gray-500">(custom plan language)</div>
        </header>
        <div className="p-3 text-sm space-y-3">
          <div className="p-2 border rounded">
            <div className="text-xs font-semibold mb-2">Add from Template</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1">Template</label>
                <select
                  className="w-full border rounded px-2 py-1.5"
                  value={tmplSel}
                  onChange={(e) => {
                    setTmplSel(e.target.value);
                    setTmplVars(newTemplateVariables());
                  }}
                >
                  <option value="">— Select —</option>
                  {NSP_TEMPLATES.map(t=> <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>

            {tmplSel === 'student_loan' && (
              <div className="mt-3 p-3 border rounded bg-gray-50 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-xs mb-1">Student Loan Creditor Record</label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      className="flex-1 border rounded px-2 py-1.5 bg-white"
                      value={tmplVars.sourceCreditorId}
                      onChange={(e) => setTmplVars(current => ({ ...current, sourceCreditorId: e.target.value }))}
                    >
                      <option value="">— Select from Schedule E/F —</option>
                      {studentLoanCreditors.map(creditor => (
                        <option key={creditor.id} value={creditor.id}>
                          {creditor.account_name || 'Unnamed student loan creditor'}
                          {creditor.balance != null ? ` — $${toTemplateMoney(creditor.balance)}` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs border rounded bg-white disabled:opacity-50"
                      disabled={!selectedStudentLoanCreditor}
                      onClick={pullServicerFromCreditor}
                    >
                      Use Selected Creditor as Servicer
                    </button>
                  </div>
                  {studentLoanCreditors.length === 0 && (
                    <div className="mt-1 text-[11px] text-amber-700">No student loan creditors were found on Schedule E/F. You can still enter the names manually.</div>
                  )}
                </div>

                <div>
                  <label className="block text-xs mb-1">Creditor / Loan Holder in Language</label>
                  <input
                    className="w-full border rounded px-2 py-1.5 bg-white"
                    value={tmplVars.creditor}
                    onChange={(e) => setTmplVars(current => ({ ...current, creditor: e.target.value }))}
                    placeholder="e.g., U.S. Department of Education"
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1">Servicer in Language</label>
                  <input
                    className="w-full border rounded px-2 py-1.5 bg-white"
                    value={tmplVars.servicer}
                    onChange={(e) => setTmplVars(current => ({ ...current, servicer: e.target.value }))}
                    placeholder="e.g., MOHELA"
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1">Payment Treatment</label>
                  <select
                    className="w-full border rounded px-2 py-1.5 bg-white"
                    value={tmplVars.studentLoanPaymentMethod}
                    onChange={(e) => setTmplVars(current => ({ ...current, studentLoanPaymentMethod: e.target.value }))}
                  >
                    <option value="through_plan">THROUGH THE CHAPTER 13 PLAN AS A GENERAL UNSECURED CREDITOR</option>
                    <option value="direct">DIRECT BY DEBTOR</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs mb-1">Language Applies To</label>
                  <select
                    className="w-full border rounded px-2 py-1.5 bg-white"
                    value={tmplVars.debtorSelection}
                    onChange={(e) => setTmplVars(current => ({ ...current, debtorSelection: e.target.value }))}
                  >
                    <option value="debtor1">
                      Debtor 1{filingDebtors[0] ? ` — ${[filingDebtors[0].first_name, filingDebtors[0].second_name, filingDebtors[0].last_name, filingDebtors[0].suffix].filter(Boolean).join(' ')}` : ''}
                    </option>
                    {filingDebtors.length > 1 && (
                      <option value="debtor2">
                        Debtor 2 — {[filingDebtors[1].first_name, filingDebtors[1].second_name, filingDebtors[1].last_name, filingDebtors[1].suffix].filter(Boolean).join(' ')}
                      </option>
                    )}
                    {filingDebtors.length > 1 && <option value="both">Both Debtors</option>}
                  </select>
                </div>
                <div className="md:col-span-2 text-[11px] text-gray-600">
                  Paragraph 6 and its subparts are included only when the loan is paid directly by the selected debtor(s).
                </div>
              </div>
            )}

            {tmplSel && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  className="px-4 py-2 text-xs border rounded bg-white"
                  onClick={async () => {
                    if (!plan?.id) return;
                    const tpl = NSP_TEMPLATES.find(t => t.id === tmplSel);
                    if (!tpl) { alert('Pick a template'); return; }

                    let description = fillTemplate(tpl.text, tmplVars);
                    if (tpl.id === 'student_loan') {
                      if (!tmplVars.creditor.trim()) {
                        alert('Enter the creditor or loan holder to use in the student loan language.');
                        return;
                      }
                      if (!tmplVars.servicer.trim()) {
                        alert('Enter the servicer to use in the student loan language.');
                        return;
                      }
                      description = buildStudentLoanTreatmentProvision({
                        creditor: tmplVars.creditor,
                        servicer: tmplVars.servicer,
                        paymentMethod: tmplVars.studentLoanPaymentMethod,
                        debtorSelection: tmplVars.debtorSelection,
                      });
                    }

                    try {
                      const created = await createNSP(plan.id, { description });
                      setNspList(previous => [...previous, created]);
                      setTmplSel('');
                      setTmplVars(newTemplateVariables());
                    } catch (error) {
                      alert(error.message);
                    }
                  }}
                >
                  Add Template
                </button>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-2 border text-left">Description</th>
                  <th className="p-2 border text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {nspList.map(nsp => (
                  <tr key={nsp.id}>
                    <td className="p-2 border">
                      <textarea className="w-full min-h-[80px] border rounded px-2 py-1.5" value={nsp.description||''} onChange={(e)=>setNspList(prev=>prev.map(x=>x.id===nsp.id?{...x, description:e.target.value}:x))} />
                    </td>
                    <td className="p-2 border text-center space-x-2">
                      <button className="px-2 py-1 border rounded" onClick={async()=>{ try{ const saved = await updateNSP(plan.id, nsp.id, nsp); setNspList(prev=>prev.map(x=>x.id===nsp.id?saved:x)); }catch(e){ alert(e.message);} }}>Save</button>
                      <button className="px-2 py-1 border rounded" onClick={async()=>{ if(!window.confirm('Delete provision?')) return; try{ await deleteNSP(plan.id, nsp.id); setNspList(prev=>prev.filter(x=>x.id!==nsp.id)); }catch(e){ alert(e.message);} }}>Delete</button>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="p-2 border"><textarea id="nsp_new_text" className="w-full min-h-[60px] border rounded px-2 py-1.5" placeholder="New non-standard provision…"></textarea></td>
                  <td className="p-2 border text-center">
                    <button className="px-3 py-1.5 text-xs border rounded" onClick={async()=>{
                      const description = document.getElementById('nsp_new_text').value;
                      if(!description) return;
                      try{
                        const created = await createNSP(plan.id, { description });
                        setNspList(prev=>[...prev, created]);
                        document.getElementById('nsp_new_text').value='';
                      }catch(e){ alert(e.message); }
                    }}>Add</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
      {/* Creditor Editor Modal */}
      {creditorEditorOpen && editingCreditor && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={closeCreditorEditor} />
          <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="font-semibold">Edit Creditor</div>
              <button className="text-sm px-3 py-1.5 rounded border" onClick={closeCreditorEditor}>Close</button>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium mb-1">Account Name</label>
                <input className="w-full border rounded px-2 py-1.5" value={editingCreditor.account_name || ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, account_name: e.target.value })}/>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Account #</label>
                <input className="w-full border rounded px-2 py-1.5" value={editingCreditor.account_number || ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, account_number: e.target.value })}/>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Debt Type</label>
                <select className="w-full border rounded px-2 py-1.5" value={editingCreditor.debt_type || ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, debt_type: e.target.value })}>
                  <option value="secured">Secured</option>
                  <option value="priority">Priority</option>
                  <option value="unsecured">Unsecured</option>
                  <option value="executory">Executory Contract</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Account Type</label>
                <select
                  className="w-full border rounded px-2 py-1.5"
                  value={normalizeAccountType(editingCreditor.debt_type, editingCreditor.account_type) || ''}
                  onChange={(e)=>setEditingCreditor({ ...editingCreditor, account_type: e.target.value })}
                >
                  {/* Preserve current value if it's custom/not in canonical lists (case-insensitive) */}
                  {(() => {
                    const current = (editingCreditor.account_type || '').toString();
                    const lower = current.toLowerCase();
                    const canon = [
                      'mortgage','auto','judgment lien','statutory lien',
                      'tax','child support','intoxicated',
                      'medical','credit card','charge account','personal loan','student loan','collection','deficiency','unsecured'
                    ];
                    const isCanon = canon.includes(lower);
                    return current && !isCanon ? (
                      <option value={current}>{current}</option>
                    ) : null;
                  })()}

                  <option value="">Select…</option>

                  {editingCreditor.debt_type === 'secured' && (
                    <>
                      <option value="mortgage">Mortgage</option>
                      <option value="auto">Auto</option>
                      <option value="judgment lien">Judgment Lien</option>
                      <option value="statutory lien">Statutory Lien</option>
                    </>
                  )}

                  {editingCreditor.debt_type === 'priority' && (
                    <>
                      <option value="tax">Tax</option>
                      <option value="child support">Child Support</option>
                      <option value="intoxicated">Intoxicated</option>
                    </>
                  )}

                  {editingCreditor.debt_type === 'unsecured' && (
                    <>
                      <option value="medical">Medical</option>
                      <option value="credit card">Credit Card</option>
                      <option value="charge account">Charge Account</option>
                      <option value="personal loan">Personal Loan</option>
                      <option value="student loan">Student Loan</option>
                      <option value="collection">Collection</option>
                      <option value="deficiency">Deficiency</option>
                      <option value="unsecured">Unsecured</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Who Owes</label>
                <select className="w-full border rounded px-2 py-1.5" value={editingCreditor.who_owes || ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, who_owes: e.target.value })}>
                  <option value="debtor1">Debtor 1</option>
                  <option value="debtor2">Debtor 2</option>
                  <option value="joint">Joint</option>
                  <option value="debtor1_third">Debtor 1 and Third</option>
                  <option value="debtor2_third">Debtor 2 and Third</option>
                  <option value="authorized">Authorized User</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Nature of Lien</label>
                <select className="w-full border rounded px-2 py-1.5" value={editingCreditor.nature_of_lien || ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, nature_of_lien: e.target.value })}>
                  <option value="agreement">Agreement</option>
                  <option value="statutory">Statutory</option>
                  <option value="judgment">Judgment</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="fdic_bank"
                  type="checkbox"
                  checked={!!editingCreditor.fdic_bank}
                  onChange={(e)=>setEditingCreditor({ ...editingCreditor, fdic_bank: e.target.checked })}
                />
                <label htmlFor="fdic_bank" className="text-xs font-medium">FDIC Bank</label>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium mb-1">Lien Description</label>
                <textarea className="w-full border rounded px-2 py-1.5" value={editingCreditor.lien_description || ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, lien_description: e.target.value })}/>
              </div>
              {(
                (editingCreditor.account_type || '').toString().toLowerCase() === 'judgment lien'
                || (editingCreditor.nature_of_lien || '').toString().toLowerCase() === 'judgment'
              ) && (
                <>
                  <div>
                    <label className="block text-xs font-medium mb-1">Civil Case Number</label>
                    <input type="text" className="w-full border rounded px-2 py-1.5" value={editingCreditor.lien_case_number || ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, lien_case_number: e.target.value })}/>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Recording County</label>
                    <input type="text" className="w-full border rounded px-2 py-1.5" value={editingCreditor.lien_county || ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, lien_county: e.target.value })}/>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Judgment Book</label>
                    <input type="text" className="w-full border rounded px-2 py-1.5" value={editingCreditor.lien_book || ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, lien_book: e.target.value })}/>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Judgment Page</label>
                    <input type="text" className="w-full border rounded px-2 py-1.5" value={editingCreditor.lien_page || ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, lien_page: e.target.value })}/>
                  </div>
                </>
              )}
              <div>
                <label className="block text-xs font-medium mb-1">Balance</label>
                <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={editingCreditor.balance ?? ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, balance: e.target.value })}/>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Arrearage</label>
                <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={editingCreditor.arrearage ?? ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, arrearage: e.target.value })}/>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Date Opened</label>
                <input type="date" className="w-full border rounded px-2 py-1.5" value={editingCreditor.date_opened || ''} onChange={(e)=>setEditingCreditor({ ...editingCreditor, date_opened: e.target.value })}/>
              </div>
              <div className="md:col-span-2 flex items-center justify-end gap-2 pt-2">
                <button className="text-xs px-3 py-1.5 rounded border" onClick={closeCreditorEditor}>Cancel</button>
                <button
                  className="text-xs px-3 py-1.5 rounded border bg-indigo-600 text-white hover:bg-indigo-700"
                  onClick={async () => {
                    try {
                      const updated = await patchCreditor(creditors.find(x=>x.id===editingCreditor.id), editingCreditor);
                      updateCreditorLocal(editingCreditor.id, updated);
                      closeCreditorEditor();
                    } catch (e) { alert(e.message); }
                  }}
                >Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Asset Editor Modal */}
      {assetEditorOpen && editingCreditor && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={closeAssetEditor} />
          <div className="absolute right-0 top-0 h-full w-full max-w-3xl bg-white shadow-xl overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="font-semibold">Edit Linked Asset(s)</div>
              <button className="text-sm px-3 py-1.5 rounded border" onClick={closeAssetEditor}>Close</button>
            </div>

            {assetListMode ? (
              <div className="p-4">
                {(editingCreditor.asset_links || []).length === 0 && (
                  <div className="text-sm text-gray-500">No linked assets.</div>
                )}
                <div className="divide-y">
                  {(editingCreditor.asset_links || []).map(l => (
                    <div key={l.id} className="py-3 grid grid-cols-12 gap-3 items-center text-sm">
                      <div className="col-span-6">
                        <div className="font-medium">Asset #{l.object_id}</div>
                        <div className="text-xs text-gray-500">{l.asset_type}</div>
                      </div>
                      <div className="col-span-6 flex justify-end gap-2">
                        <button className="text-xs px-3 py-1.5 rounded border" onClick={()=>editThisAssetLink(l)}>Edit</button>
                      </div>
              <div className="col-span-12 grid grid-cols-6 gap-3 text-xs text-gray-600">
                <div className="col-span-2">Eff. Value: ${toMoney(effectiveAssetLinkValue(l))}</div>
                {(['realproperty','vehicle'].includes((l.asset_type||'').toLowerCase())) && (
                  <>
                    <div>Total: ${toMoney(l.value_total)}</div>
                    <div>Own. Portion: ${toMoney(l.value_ownership_portion)}</div>
                  </>
                )}
                {(String(l.asset_type||'').toLowerCase()==='householditem') && (
                  <div>Value: ${toMoney(l.value)}</div>
                )}
                {(!['realproperty','vehicle','householditem'].includes((l.asset_type||'').toLowerCase())) && (
                  <div>Amount: ${toMoney(l.amount)}</div>
                )}
                <div>Exempt: ${toMoney(l.exemption_amount)}</div>
                <div>Pmt1/Pmt2: ${toMoney(l.monthly_payment)} / ${toMoney(l.second_monthly_payment)}</div>
              </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="md:col-span-2 font-semibold">Asset #{editingAssetLink?.object_id} ({editingAssetLink?.asset_type})</div>
                {(() => {
                  const t = (editingAssetLink?.asset_type || '').toString().toLowerCase();
                  return (
                    <>
                      {/* Common fields */}
                      <div>
                        <label className="block text-xs font-medium mb-1">Exemption</label>
                        <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={editingAssetLink?.exemption_amount ?? ''} onChange={(e)=>setEditingAssetLink({ ...editingAssetLink, exemption_amount: e.target.value })}/>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Monthly Payment</label>
                        <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={editingAssetLink?.monthly_payment ?? ''} onChange={(e)=>setEditingAssetLink({ ...editingAssetLink, monthly_payment: e.target.value })}/>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Second Monthly Payment</label>
                        <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={editingAssetLink?.second_monthly_payment ?? ''} onChange={(e)=>setEditingAssetLink({ ...editingAssetLink, second_monthly_payment: e.target.value })}/>
                      </div>

                      {/* Type-specific value inputs */}
                      { (t === 'realproperty' || t === 'vehicle') && (
                        <>
                          <div className="md:col-span-2 pt-2 border-t" />
                          <div>
                            <label className="block text-xs font-medium mb-1">Value Total (100% of asset)</label>
                            <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={editingAssetLink?.value_total ?? ''} onChange={(e)=>setEditingAssetLink({ ...editingAssetLink, value_total: e.target.value })}/>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Value (ownership portion)</label>
                            <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={editingAssetLink?.value_ownership_portion ?? ''} onChange={(e)=>setEditingAssetLink({ ...editingAssetLink, value_ownership_portion: e.target.value })}/>
                          </div>
                        </>
                      )}

                      { t === 'householditem' && (
                        <>
                          <div className="md:col-span-2 pt-2 border-t" />
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium mb-1">Value</label>
                            <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={editingAssetLink?.value ?? ''} onChange={(e)=>setEditingAssetLink({ ...editingAssetLink, value: e.target.value })}/>
                          </div>
                        </>
                      )}

                      { (t !== 'realproperty' && t !== 'vehicle' && t !== 'householditem') && (
                        <>
                          <div className="md:col-span-2 pt-2 border-t" />
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium mb-1">Amount</label>
                            <input type="number" step="0.01" className="w-full border rounded px-2 py-1.5 text-right" value={editingAssetLink?.amount ?? ''} onChange={(e)=>setEditingAssetLink({ ...editingAssetLink, amount: e.target.value })}/>
                          </div>
                        </>
                      )}

                      {/* Effective value summary */}
                      <div className="md:col-span-2 text-xs text-gray-600">
                        <div className="mt-1">Effective value used in plan calcs: <span className="font-semibold">${toMoney((() => effectiveAssetLinkValue(editingAssetLink || {}))())}</span></div>
                        <div className="mt-1">Rule: realproperty/vehicle → ownership portion (fallback total); householditem → value; other → amount</div>
                      </div>
                    </>
                  );
                })()}
                <div className="md:col-span-2 flex items-center justify-between pt-2">
                  <button className="text-xs px-3 py-1.5 rounded border" onClick={backToAssetList}>Back</button>
                  <div className="flex items-center gap-2">
                    <button className="text-xs px-3 py-1.5 rounded border" onClick={backToAssetList}>Cancel</button>
                    <button
                      className="text-xs px-3 py-1.5 rounded border bg-indigo-600 text-white hover:bg-indigo-700"
                      onClick={async () => {
                        try {
                          const saved = await patchAssetLink(editingAssetLink.id, editingAssetLink);
                          // Reflect change in local state (both list in row and modals)
                          setCreditors(prev => prev.map(c => {
                            if (c.id !== editingCreditor.id) return c;
                            return {
                              ...c,
                              asset_links: (c.asset_links || []).map(al => al.id === editingAssetLink.id ? { ...al, ...saved } : al)
                            };
                          }));
                          setEditingCreditor(prev => prev ? { ...prev, asset_links: (prev.asset_links || []).map(al => al.id === editingAssetLink.id ? { ...al, ...saved } : al) } : prev);
                          backToAssetList();
                        } catch (e) { alert(e.message); }
                      }}
                    >Save Changes</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
